// src/components/MedicalBillingApp.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import * as XLSX from 'xlsx';
import { escapeHtml, secureLog } from '../utils/validation';
import {
  auth,
  loginUser,
  logoutUser,
  getUserProfile,
  uploadFeeScheduleWithCacheReset,
  getActiveFeeSchedule,
  getCPTData,
  getCPTDataInfo,
  saveCalculation,
  sendVerificationEmail,
  checkEmailVerified,
  deleteUserClientSide,
  createPendingUser,
  activatePendingUser,
  getPendingUsers,
  deletePendingUser,
  getAllUsersActive,
  parseCSVFile,
  updateUserRole,
  getBaseProcedureAllowable,
  saveInsuranceEndoscopySettings,
  getInsuranceEndoscopySettings,
  saveInsuranceBilateralSettings,
  getInsuranceBilateralSettings,
  getModifiersForCPT,
  getCPTDataWithModifier,
  UserProfile,
  FeeSchedule,
  UserRole,
  CPTData,
  sendPasswordReset,
  confirmPasswordResetWithCode,
} from '../firebase';

// Type definitions
interface CPTCodeEntry {
  code: string;
  units: string;
  bilateral?: boolean; // NEW: Bilateral flag
  modifier?: string; // Modifier (e.g., 26, TC, 50, LT, RT)
}

interface CalculatorData {
  patientId: string;          
  cptCodes: CPTCodeEntry[];
  insuranceCarrier: string;
  medigapPlan: string;
  deductibleRemaining: string;
  outOfPocketRemaining: string;
  coinsurance: string;
  hasCopay: string;        
  copayAmount: string;     
}

interface ProcedureResult {
  cpt: string;
  charge: number;
  allowable: number;
  mpprAllowable: number;
  bilateral?: boolean; // NEW: Track bilateral procedures
  modifier?: string; // Modifier (e.g., 26, TC, 50, LT, RT)
}

interface Results {
  procedures: ProcedureResult[];
  totalCharge: number;
  mpprAdjustedTotal: number;
  deductibleDue: number;
  coinsuranceDue: number;
  copayDue: number; // NEW: Track copay separately
  patientPayment: number;
  slidingFeePayment: number;
  isCappedByOOP: boolean;
  usedEnhancedEndoscopy?: boolean;
  usedBilateralOption?: 'option1' | 'option2' | null; // NEW: Track bilateral logic used
}

interface FieldErrors {
  cptCodes?: boolean;
  deductibleRemaining?: boolean;
  outOfPocketRemaining?: boolean;
  coinsurance?: boolean;
  copayAmount?: boolean;
}

interface FeeScheduleRow {
  [key: string]: any;
  'CPT Code'?: string | number;
  'CPT_Code'?: string | number;
  'cpt'?: string | number;
  'CPT'?: string | number;
  'Code'?: string | number;
  'Charge'?: number;
  'Description'?: string;
}

// Extended procedure result for internal calculation
interface ExtendedProcedureResult extends ProcedureResult {
  multipleProcedureIndicator: string;
  endoBase?: string;
  isEMCode: boolean;
  bilateralProcedureIndicator?: string; // NEW
  isBilateralSplit?: boolean; // FIXED: Track bilateral split units for ranking
}

// Endoscopy family interface for enhanced MPPR logic
interface EndoscopyFamily {
  baseCode: string;
  procedures: ExtendedProcedureResult[];
  totalAllowable: number;
  familyRank: number;
}

// Store CPT metadata for display
interface CPTMetadata {
  [cptCode: string]: {
    multipleProcedureIndicator: string;
    endoBase?: string;
    bilateralProcedureIndicator?: string;
    description?: string; // NEW: Store procedure description
  };
}

const MedicalBillingApp: React.FC = () => {
  // Modern font family
  const modernFontFamily = "'Inter', 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

  // Modern input styles
  const modernInputStyle = {
    backgroundColor: '#eee',
    border: 'none',
    padding: '1rem',
    fontSize: '1rem',
    borderRadius: '1rem',
    color: 'lightcoral',
    boxShadow: '0 0.4rem #dfd9d9',
    cursor: 'pointer',
    outline: 'none',
    width: '100%',
    fontFamily: modernFontFamily
  };

  // Matching dropdown styles
  const modernDropdownStyle = {
    backgroundColor: '#eee',
    border: 'none',
    padding: '1rem',
    fontSize: '1rem',
    borderRadius: '1rem',
    color: 'lightcoral',
    boxShadow: '0 0.4rem #dfd9d9',
    cursor: 'pointer',
    outline: 'none',
    width: '100%',
    fontFamily: modernFontFamily,
    appearance: 'none' as const,
    backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'lightcoral\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6,9 12,15 18,9\'%3e%3c/polyline%3e%3c/svg%3e")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 1rem center',
    backgroundSize: '1rem',
    paddingRight: '3rem'
  };

  // iOS checkbox styles
  const checkboxContainerStyle = {
    display: 'flex',
    gap: '20px',
    padding: '10px',
    background: '#f8fafc',
    borderRadius: '12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.05)'
  };

  const iosCheckboxStyle = {
    position: 'relative' as const,
    display: 'inline-block',
    cursor: 'pointer',
    userSelect: 'none' as const,
    WebkitTapHighlightColor: 'transparent'
  };

  const checkboxWrapperStyle = {
    position: 'relative' as const,
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    transition: 'transform 0.2s ease'
  };

  const checkboxBgStyle = {
    position: 'absolute' as const,
    inset: '0',
    borderRadius: '8px',
    border: '2px solid #6b7280',
    background: 'white',
    transition: 'all 0.2s ease'
  };

  const checkboxBgCheckedStyle = {
    ...checkboxBgStyle,
    background: 'lightcoral',
    border: 'none'
  };

  const checkboxIconStyle = {
    position: 'absolute' as const,
    inset: '0',
    margin: 'auto',
    width: '80%',
    height: '80%',
    color: 'white',
    transform: 'scale(0)',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const checkboxIconCheckedStyle = {
    ...checkboxIconStyle,
    transform: 'scale(1)'
  };

  const bilateralLabelStyle = {
    fontSize: '1rem',
    color: '#6b7280',
    fontFamily: modernFontFamily,
    marginLeft: '8px'
  };

  // State management
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [appState, setAppState] = useState<'loading' | 'login' | 'calculator' | 'verification'>('loading');
  const [pendingVerificationUser, setPendingVerificationUser] = useState<User | null>(null);
  
  // Billing calculator state
  const [calculatorData, setCalculatorData] = useState<CalculatorData>({
    patientId: '',
    cptCodes: [{ code: '', units: '1' }],
    insuranceCarrier: 'None',
    medigapPlan: 'None',
    deductibleRemaining: '',
    outOfPocketRemaining: '',
    coinsurance: '',
    hasCopay: 'No',
    copayAmount: ''
  });
  
  const [results, setResults] = useState<Results | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  
  // Fee schedule and CPT data state
  const [feeSchedule, setFeeSchedule] = useState<FeeSchedule | null>(null);
  const [cptData, setCptData] = useState<CPTData[]>([]);
  const [insuranceCarriers, setInsuranceCarriers] = useState<string[]>([]);
  const [cptMetadata, setCptMetadata] = useState<CPTMetadata>({}); // NEW: Store CPT metadata
  const [cptModifiers, setCptModifiers] = useState<{[key: number]: string[]}>({});  // Available modifiers per CPT code entry index
  
  // Admin and user management state
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [endoscopySettingsOpen, setEndoscopySettingsOpen] = useState(false); // NEW: Endoscopy settings
  const [bilateralSettingsOpen, setBilateralSettingsOpen] = useState(false); // NEW: Bilateral settings
  const [userManagementOpen, setUserManagementOpen] = useState(false); // NEW: User management settings
  const [insuranceEndoscopySettings, setInsuranceEndoscopySettings] = useState<{[key: string]: boolean}>({});
  const [insuranceBilateralSettings, setInsuranceBilateralSettings] = useState<{[key: string]: 'option1' | 'option2'}>({});
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [pendingUsers, setPendingUsers] = useState<Array<{email: string, role: UserRole, createdAt: any, createdBy: string}>>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [pendingUserData, setPendingUserData] = useState<{ email: string; role: UserRole } | null>(null);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [addUserMessage, setAddUserMessage] = useState('');
  const [activateEmail, setActivateEmail] = useState('');
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  
  // Auth form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  // Password reset/activation state
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetMode, setResetMode] = useState<'resetPassword' | 'recoverEmail' | 'verifyEmail' | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordResetError, setPasswordResetError] = useState('');
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

  // File upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  // Utility functions
  const isEMCode = (cpt: string): boolean => {
    const cptNum = parseInt(cpt);
    return cptNum >= 99201 && cptNum <= 99499;
  };

  // Authentication effect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (user.emailVerified) {
          setCurrentUser(user);
          try {
            const profile = await getUserProfile(user.uid);
            setUserProfile(profile);
            setAppState('calculator');
            setPendingVerificationUser(null);
            setVerificationMessage('');
          } catch (error) {
            console.error('Error loading user profile:', error);
            await logoutUser();
            setAppState('login');
            setAuthError('Error loading user profile. Please try again.');
          }
        } else {
          setPendingVerificationUser(user);
          setAppState('verification');
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setAppState('login');
        setPendingVerificationUser(null);
        setVerificationMessage('');
      }
    });

    return () => unsubscribe();
  }, []);

  // Check for password reset/activation link in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const oobCode = urlParams.get('oobCode');

    if (mode && oobCode) {
      if (mode === 'resetPassword') {
        setResetMode('resetPassword');
        setResetCode(oobCode);
        setShowPasswordReset(true);
        setAppState('login'); // Stay on login state but show password reset UI
      }
    }
  }, []);

  // Load fee schedule and CPT data
  const loadFeeSchedule = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const schedule = await getActiveFeeSchedule();
      setFeeSchedule(schedule);
      
      if (schedule) {
        await loadInsuranceCarriers();
      }
    } catch (error) {
      console.error('Error loading fee schedule:', error);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && appState === 'calculator') {
      loadFeeSchedule();
      // Load insurance carrier settings from Firestore
      loadInsuranceEndoscopySettings();
      loadInsuranceBilateralSettings();
    }
  }, [currentUser, appState, loadFeeSchedule]);

  // Load insurance carriers from CPT data
  const loadInsuranceCarriers = async (): Promise<void> => {
    try {
      const info = await getCPTDataInfo();
      if (info.available) {
        // Get a broader sample of CPT data to extract all insurance carriers
        // Start with common E&M codes, but also get a random sample from all available CPT codes
        const commonCPTs = ['99213', '99214', '99215', '99201', '99202', '99203', '99204', '99205', '99211', '99212'];

        // Get all available CPT codes to sample from
        const { db } = await import('../firebase');
        const { getDocs, collection } = await import('firebase/firestore');
        const allCPTSnapshot = await getDocs(collection(db, 'cptData'));
        const allAvailableCPTs = allCPTSnapshot.docs.map(doc => doc.id);

        // Create a comprehensive sample: common CPTs + random sample from available CPTs
        const sampleSize = Math.min(50, allAvailableCPTs.length); // Sample up to 50 CPT codes
        const randomSample = allAvailableCPTs
          .filter(cpt => !commonCPTs.includes(cpt)) // Exclude already included common CPTs
          .sort(() => 0.5 - Math.random()) // Shuffle
          .slice(0, Math.max(0, sampleSize - commonCPTs.length)); // Take remaining slots

        const sampleCPTs = [...commonCPTs.filter(cpt => allAvailableCPTs.includes(cpt)), ...randomSample];

        console.log(`Sampling ${sampleCPTs.length} CPT codes from ${allAvailableCPTs.length} available codes to find insurance carriers`);
        const sampleData = await getCPTData(sampleCPTs);

        if (sampleData.length > 0) {
          const carriers = new Set<string>();

          sampleData.forEach(record => {
            Object.keys(record).forEach(key => {
              const lowerKey = key.toLowerCase().replace(/\s+/g, '');

              // Fields to exclude from insurance carriers
              const excludeFields = [
                'cptcode', 'cpt_code', 'cpt', 'code', 'charge', 'description',
                'uploadedat', 'scheduleid',
                'multipleprocedureindicator', 'multipleProcedureIndicator',
                'endo_base', 'endobase', 'basecode', 'base_code',
                'bilateralprocedureindicator', 'bilateralProcedureIndicator',
                'bilateralindicator', 'bilateral_indicator',
                'modifier', 'modifiers'
              ];

              if (!excludeFields.includes(lowerKey) &&
                  typeof record[key] !== 'undefined' &&
                  record[key] !== null &&
                  record[key] !== '') {
                carriers.add(key);
              }
            });
          });

          const carrierList = Array.from(carriers).filter(carrier => carrier !== 'None').sort();
          setInsuranceCarriers(carrierList);
          console.log(`Insurance carriers loaded from ${sampleData.length} CPT codes:`, carrierList);
        }
      }
    } catch (error) {
      console.error('Error loading insurance carriers:', error);
      setInsuranceCarriers([]);
    }
  };

  // NEW: Load insurance endoscopy settings
  const loadInsuranceEndoscopySettings = async () => {
    try {
      const settings = await getInsuranceEndoscopySettings();
      setInsuranceEndoscopySettings(settings);
    } catch (error) {
      console.error('Error loading endoscopy settings:', error);
    }
  };

  // NEW: Save insurance endoscopy settings
  const handleSaveEndoscopySettings = async () => {
    try {
      await saveInsuranceEndoscopySettings(insuranceEndoscopySettings);
      alert('Endoscopy settings saved successfully!');
    } catch (error) {
      console.error('Error saving endoscopy settings:', error);
      alert('Error saving endoscopy settings');
    }
  };

  // NEW: Load insurance bilateral settings
  const loadInsuranceBilateralSettings = async () => {
    try {
      const settings = await getInsuranceBilateralSettings();
      setInsuranceBilateralSettings(settings);
    } catch (error) {
      console.error('Error loading bilateral settings:', error);
    }
  };

  // NEW: Save insurance bilateral settings
  const handleSaveBilateralSettings = async () => {
    try {
      await saveInsuranceBilateralSettings(insuranceBilateralSettings);
      alert('Bilateral settings saved successfully!');
    } catch (error) {
      console.error('Error saving bilateral settings:', error);
      alert('Error saving bilateral settings');
    }
  };

  // Helper function to get CPT codes for calculation
  const getCPTCodesForCalculation = (): Array<{cpt: string, units: number, bilateral?: boolean, modifier?: string}> => {
    return calculatorData.cptCodes
      .filter(entry => entry.code.trim() !== '')
      .flatMap(entry => {
        const units = parseInt(entry.units) || 1;
        const items = [];

        // FIXED: Only create multiple units when bilateral checkbox is NOT checked
        // If bilateral is checked, we handle it in the calculation logic, not here
        if (entry.bilateral) {
          // For bilateral procedures, create single entry that will be processed by bilateral logic
          items.push({
            cpt: entry.code.trim(),
            units: 1,
            bilateral: true,
            modifier: entry.modifier || undefined
          });
        } else {
          // For non-bilateral procedures, create separate entries for each unit
          for (let i = 0; i < units; i++) {
            items.push({
              cpt: entry.code.trim(),
              units: 1,
              bilateral: false,
              modifier: entry.modifier || undefined
            });
          }
        }

        return items;
      });
  };

  // Main calculation function with enhanced MPPR and bilateral support
  const handleCalculate = async (): Promise<void> => {
    // Input validation
    const errors: FieldErrors = {};
    
    const cptList = getCPTCodesForCalculation();
    if (cptList.length === 0) {
      errors.cptCodes = true;
    }
    
    if (calculatorData.insuranceCarrier !== 'None') {
      const isMedicaid = calculatorData.insuranceCarrier.toLowerCase().includes('medicaid');

      // Plan C and Plan F have fixed $0 deductible, don't require input
      // Medicaid has default $0 deductible but it's already populated
      const hasFixedDeductible = (calculatorData.insuranceCarrier.toLowerCase().includes('medicare') &&
                                 (calculatorData.medigapPlan === 'Plan C' || calculatorData.medigapPlan === 'Plan F')) ||
                                 isMedicaid;
      if (!hasFixedDeductible && !calculatorData.deductibleRemaining) errors.deductibleRemaining = true;

      // Only require Out of Pocket for non-Medicare/non-Medicaid or Medicare with Plan K/L
      const requiresOOP = !isMedicaid && !(calculatorData.insuranceCarrier.toLowerCase().includes('medicare') &&
                           calculatorData.medigapPlan !== 'Plan K' &&
                           calculatorData.medigapPlan !== 'Plan L');
      if (requiresOOP && !calculatorData.outOfPocketRemaining) errors.outOfPocketRemaining = true;

      // Medicaid has default coinsurance but it's already populated
      if (!calculatorData.coinsurance) errors.coinsurance = true;
      if (calculatorData.hasCopay === 'Yes' && !calculatorData.copayAmount) errors.copayAmount = true;
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    const allProcedures: ExtendedProcedureResult[] = [];
    const missingCPTs: string[] = [];
    const missingInsuranceValues: string[] = [];
    const newCptMetadata: CPTMetadata = {};

    // Check for E&M codes with copay
    const hasCopayEnabled = calculatorData.hasCopay === 'Yes' && calculatorData.copayAmount.trim() !== '';
    const copayAmountNum = hasCopayEnabled ? Number(calculatorData.copayAmount) : 0;
    const hasEMCodes = cptList.some(item => isEMCode(item.cpt));

    if (hasCopayEnabled && !hasEMCodes) {
      alert('Copay can only be used when E&M codes (99201-99499) are included in the procedure list.');
      return;
    }

    // NEW: Check bilateral settings for current insurance carrier
    const carrierBilateralSetting = insuranceBilateralSettings[calculatorData.insuranceCarrier] || 'option2'; // Default to option2
    console.log(`🔄 Insurance carrier ${calculatorData.insuranceCarrier} bilateral setting: ${carrierBilateralSetting}`);

    // Get CPT data with modifiers
    console.log('🔍 Querying CPT codes:', cptList.map(item => `${item.cpt}${item.modifier ? '-' + item.modifier : ''}`));
    let cptDataMap: Map<string, CPTData> = new Map();
    try {
      // Fetch each unique CPT+modifier combination
      const uniqueEntries = Array.from(
        new Set(cptList.map(item => JSON.stringify({ cpt: item.cpt, modifier: item.modifier || '' })))
      ).map(str => JSON.parse(str));

      const fetchPromises = uniqueEntries.map(async ({ cpt, modifier }) => {
        const data = await getCPTDataWithModifier(cpt, modifier || undefined);
        if (data) {
          const key = modifier ? `${cpt}__${modifier}` : cpt;
          cptDataMap.set(key, data);
        }
      });

      await Promise.all(fetchPromises);
      console.log('✅ CPT data retrieved:', cptDataMap.size, 'records');
    } catch (error) {
      console.error('Error querying CPT data:', error);
      alert('Error retrieving CPT data. Please try again.');
      return;
    }

    // Build procedure list with Multiple Procedure Indicator and Bilateral Procedure Indicator data
    cptList.forEach(item => {
      const lookupKey = item.modifier ? `${item.cpt}__${item.modifier}` : item.cpt;
      const procedure = cptDataMap.get(lookupKey);

      if (!procedure) {
        if (!missingCPTs.includes(item.cpt)) {
          missingCPTs.push(item.cpt);
        }
        return;
      }

      // Get base charge and allowable WITHOUT applying bilateral multipliers here
      let charge = Number(procedure.charge) || 0;
      let allowableValue = 0;

      // FIXED: Removed early bilateral multiplication that was causing double application
      // The bilateral logic should only be applied later when adding to allProcedures

      if (charge === 0) {
        const missingChargeMsg = `${item.cpt} (no charge value)`;
        if (!missingCPTs.includes(missingChargeMsg)) {
          missingCPTs.push(missingChargeMsg);
        }
        return;
      }

      if (calculatorData.insuranceCarrier !== 'None') {
        allowableValue = Number(procedure[calculatorData.insuranceCarrier]) || 0;
        
        // FIXED: Removed early bilateral allowable multiplication that was causing double application
        
        if (allowableValue === 0) {
          const missingInsuranceMsg = `${item.cpt} (no ${calculatorData.insuranceCarrier} allowable)`;
          if (!missingInsuranceValues.includes(missingInsuranceMsg)) {
            missingInsuranceValues.push(missingInsuranceMsg);
          }
          return;
        }
      } else {
        allowableValue = charge;
      }

      // Get Multiple Procedure Indicator, Endo Base, and Bilateral Procedure Indicator from CPT data
      const multipleProcedureIndicator = String(
        procedure.MultipleProcedureIndicator || 
        procedure.multipleProcedureIndicator || 
        procedure['Multiple Procedure Indicator'] ||
        '9'
      );
      
      const endoBase = procedure.EndoBase || 
        procedure.endoBase || 
        procedure['Endo Base'] || 
        undefined;

      const bilateralProcedureIndicator = String(
        procedure.BilateralProcedureIndicator || 
        procedure.bilateralProcedureIndicator || 
        procedure['Bilateral Procedure Indicator'] ||
        '9'
      );

      // Get description from procedure data
      const description = procedure.Description ||
        procedure.description ||
        procedure.desc ||
        procedure.Desc ||
        undefined;

      // Store metadata for display
      newCptMetadata[item.cpt] = {
        multipleProcedureIndicator,
        endoBase: endoBase && endoBase !== 'undefined' ? String(endoBase).trim() : undefined,
        bilateralProcedureIndicator,
        description: description ? String(description).trim() : undefined
      };

      // FIXED: Handle bilateral procedures based on carrier setting - APPLY MULTIPLIERS HERE ONLY
      if (item.bilateral && bilateralProcedureIndicator === '1') {
        if (carrierBilateralSetting === 'option2') {
          // Option 2: Treat as 2 separate units with standard MPPR
          for (let i = 0; i < 2; i++) {
            allProcedures.push({
              cpt: item.cpt,
              charge, // Use base charge for each unit
              allowable: allowableValue, // Use base allowable for each unit
              mpprAllowable: allowableValue, // Will be adjusted by MPPR logic
              multipleProcedureIndicator,
              endoBase,
              isEMCode: isEMCode(item.cpt),
              bilateralProcedureIndicator,
              bilateral: false, // Don't mark as bilateral since we're treating as separate units
              isBilateralSplit: true, // FIXED: Mark these as bilateral split units for ranking
              modifier: item.modifier
            });
          }
          console.log(`💊 CPT ${item.cpt}: Bilateral Option 2 - Added 2 separate units for MPPR`);
        } else {
          // Option 1: Single unit with 2x charge, 1.5x allowable (APPLY MULTIPLIERS HERE ONLY)
          const bilateralCharge = charge * 2;
          const bilateralAllowable = allowableValue * 1.5;

          allProcedures.push({
            cpt: item.cpt,
            charge: bilateralCharge,
            allowable: bilateralAllowable,
            mpprAllowable: bilateralAllowable, // Will be adjusted by MPPR logic
            multipleProcedureIndicator,
            endoBase,
            isEMCode: isEMCode(item.cpt),
            bilateralProcedureIndicator,
            bilateral: true, // Mark as bilateral for Option 1
            isBilateralSplit: false,
            modifier: item.modifier
          });
          console.log(`💊 CPT ${item.cpt}: Bilateral Option 1 - Single unit with 2x charge (${bilateralCharge.toFixed(2)}) and 1.5x allowable (${bilateralAllowable.toFixed(2)})`);
        }
      } else {
        // Single procedure (non-bilateral)
        allProcedures.push({
          cpt: item.cpt,
          charge,
          allowable: allowableValue,
          mpprAllowable: allowableValue, // Will be adjusted by MPPR logic
          multipleProcedureIndicator,
          endoBase,
          isEMCode: isEMCode(item.cpt),
          bilateralProcedureIndicator,
          bilateral: false, // Not bilateral
          isBilateralSplit: false,
          modifier: item.modifier
        });

        console.log(`💊 CPT ${item.cpt}: Single procedure - Charge ${charge.toFixed(2)}, Allowable ${allowableValue.toFixed(2)}`);
      }
    });

    // Update CPT metadata for display
    setCptMetadata(newCptMetadata);

    if (missingCPTs.length > 0 || missingInsuranceValues.length > 0) {
      let displayMessage = '';

      if (missingCPTs.length > 0) {
        displayMessage += '❌ CPT Code Issues:\n';
        missingCPTs.forEach(cpt => {
          if (cpt.includes('(no charge value)')) {
            const code = cpt.replace(' (no charge value)', '');
            displayMessage += `   • CPT ${code}: Found in fee schedule but missing charge amount\n`;
          } else {
            displayMessage += `   • CPT ${cpt}: Not found in fee schedule\n`;
          }
        });
        displayMessage += '\n';
      }

      if (missingInsuranceValues.length > 0) {
        displayMessage += `❌ Insurance Coverage Issues for ${calculatorData.insuranceCarrier}:\n`;
        missingInsuranceValues.forEach(item => {
          const code = item.split(' (no ')[0];
          displayMessage += `   • CPT ${code}: Found in fee schedule but no allowable amount for ${calculatorData.insuranceCarrier}\n`;
        });
      }

      alert(displayMessage);
      return;
    }

    if (allProcedures.length === 0) {
      alert('No valid procedures found for calculation.');
      return;
    }

    // Check if this insurance carrier uses enhanced endoscopy
    const carrierUsesEnhancedEndoscopy = insuranceEndoscopySettings[calculatorData.insuranceCarrier] !== false; // Default true
    console.log(`🏥 Insurance carrier ${calculatorData.insuranceCarrier} enhanced endoscopy: ${carrierUsesEnhancedEndoscopy}`);
    console.log(`🏥 Enhanced endoscopy settings object:`, insuranceEndoscopySettings);
    console.log(`🏥 Setting for ${calculatorData.insuranceCarrier}:`, insuranceEndoscopySettings[calculatorData.insuranceCarrier]);

    // ENHANCED MPPR LOGIC WITH COMPREHENSIVE ENDOSCOPY SUPPORT AND BILATERAL LOGIC
    console.log(`🔄 Applying Multiple Procedure Indicator logic with ${carrierUsesEnhancedEndoscopy ? 'enhanced' : 'standard'} endoscopy MPPR and ${carrierBilateralSetting} bilateral logic...`);

    // Note: E&M codes with copay will be handled in patient responsibility calculation
    // Do not override mpprAllowable here - let it go through normal MPPR processing

    // Group procedures by Multiple Procedure Indicator
    const indicatorGroups = allProcedures.reduce((groups: {[key: string]: ExtendedProcedureResult[]}, proc) => {
      if (!groups[proc.multipleProcedureIndicator]) {
        groups[proc.multipleProcedureIndicator] = [];
      }
      groups[proc.multipleProcedureIndicator].push(proc);
      return groups;
    }, {});

    console.log(`📊 Grouped procedures by indicator:`, Object.keys(indicatorGroups).map(key => `${key} (${indicatorGroups[key].length} procedures)`).join(', '));

    // DEBUGGING: Log detailed procedure info
    Object.entries(indicatorGroups).forEach(([indicator, procs]) => {
      console.log(`🔍 Indicator ${indicator} procedures:`, procs.map(p => `${p.cpt}(${p.multipleProcedureIndicator})`).join(', '));
    });

    // Process each indicator group
    for (const [indicator, procedures] of Object.entries(indicatorGroups)) {
      console.log(`\n🔍 Processing Indicator ${indicator} with ${procedures.length} procedures...`);

      if (indicator === '0' || indicator === '9') {
        // No MPPR reduction for indicators 0 and 9
        console.log(`✅ Indicator ${indicator}: No MPPR reduction applied`);
        continue;
      }

      if (indicator === '1' || indicator === '2') {
        // Standard MPPR: Sort by allowable and apply 100%/50% reduction
        const sortedProcs = procedures
          .filter(proc => !(proc.isEMCode && hasCopayEnabled))
          .sort((a, b) => {
            // Sort by allowable (highest first)
            if (b.allowable !== a.allowable) {
              return b.allowable - a.allowable;
            }
            // Tiebreaker: alphabetical by CPT code for consistent ordering
            return a.cpt.localeCompare(b.cpt);
          });

        sortedProcs.forEach((proc, index) => {
          const reduction = index === 0 ? 1.0 : 0.5;
          proc.mpprAllowable = proc.allowable * reduction;
          console.log(`🔄 IND-${indicator}: ${proc.cpt} rank ${index + 1} = ${(reduction * 100).toFixed(0)}% → ${proc.mpprAllowable.toFixed(2)}`);
        });
      }

      if (indicator === '3') {
        // Endoscopy MPPR logic
        if (carrierUsesEnhancedEndoscopy) {
          // Enhanced endoscopy logic with proper base code subtraction
          console.log(`🔬 Enhanced Endoscopy MPPR for Indicator 3...`);
          console.log(`💳 Carrier: ${calculatorData.insuranceCarrier}, Bilateral Logic: ${carrierBilateralSetting}`);

          // Create async function to handle enhanced endoscopy processing
          const processEnhancedEndoscopyMPPR = async (): Promise<void> => {
            const endoscopyFamilies: {[key: string]: EndoscopyFamily} = {};
            const standaloneEndos: ExtendedProcedureResult[] = [];

            // Filter out E&M codes with copay
            const endoProcedures = procedures.filter(proc => !(proc.isEMCode && hasCopayEnabled));

            console.log(`🔍 Processing ${endoProcedures.length} endoscopy procedures for enhanced MPPR...`);

            // Step 1: Group procedures by endoscopy families
            console.log(`🔍 Grouping ${endoProcedures.length} endoscopy procedures by families...`);
            endoProcedures.forEach(proc => {
              console.log(`📝 Processing ${proc.cpt}: endoBase="${proc.endoBase}", allowable=$${proc.allowable.toFixed(2)}`);

              if (proc.endoBase && String(proc.endoBase).trim() !== '') {
                const baseCode = String(proc.endoBase).trim();
                if (!endoscopyFamilies[baseCode]) {
                  endoscopyFamilies[baseCode] = {
                    baseCode: baseCode,
                    procedures: [],
                    totalAllowable: 0,
                    familyRank: 0
                  };
                  console.log(`🏗️ Created new family for base code: ${baseCode}`);
                }
                endoscopyFamilies[baseCode].procedures.push(proc);
                console.log(`👪 Added ${proc.cpt} to family ${baseCode} (allowable: $${proc.allowable.toFixed(2)}, bilateral: ${proc.bilateral || false})`);
              } else {
                standaloneEndos.push(proc);
                console.log(`🔍 Standalone endo: ${proc.cpt} (allowable: $${proc.allowable.toFixed(2)}, bilateral: ${proc.bilateral || false}) - no endoBase`);
              }
            });

            // Step 2: Pre-fetch base procedure allowables for all families
            const baseProcedureAllowables: {[key: string]: number} = {};
            const uniqueBaseCodes = Array.from(new Set(Object.keys(endoscopyFamilies)));

            console.log(`📋 Fetching base procedure allowables for ${uniqueBaseCodes.length} base codes...`);

            for (const baseCode of uniqueBaseCodes) {
              try {
                console.log(`🔍 Looking up base procedure allowable for ${baseCode} with carrier ${calculatorData.insuranceCarrier}`);
                console.log(`🔍 Fee schedule object:`, feeSchedule ? 'exists' : 'null');

                if (!calculatorData.insuranceCarrier || calculatorData.insuranceCarrier === 'None') {
                  console.error(`❌ No insurance carrier specified for base code lookup`);
                  baseProcedureAllowables[baseCode] = 0;
                  continue;
                }

                // Pass the insurance carrier name, not the fee schedule ID
                baseProcedureAllowables[baseCode] = await getBaseProcedureAllowable(baseCode, calculatorData.insuranceCarrier);
                console.log(`🏗️ Base procedure ${baseCode} allowable: $${baseProcedureAllowables[baseCode].toFixed(2)}`);

                if (baseProcedureAllowables[baseCode] === 0) {
                  console.warn(`⚠️ Base procedure ${baseCode} returned $0.00 allowable - will use fallback logic`);
                }
              } catch (error) {
                console.error(`❌ Error getting base procedure allowable for ${baseCode}:`, error);
                console.error(`❌ Error details:`, error instanceof Error ? error.message : String(error));
                console.error(`❌ Insurance carrier: ${calculatorData.insuranceCarrier || 'undefined'}`);
                baseProcedureAllowables[baseCode] = 0; // Will trigger fallback
              }
            }

            console.log(`📋 Base procedure allowables lookup completed:`, baseProcedureAllowables);

            // Step 3: Apply proper endoscopy base code subtraction within each family
            console.log(`\n🔬 Applying base code subtraction method within families...`);
            Object.values(endoscopyFamilies).forEach(family => {
              if (family.procedures.length > 0) {
                // Sort procedures within family by allowable amount (highest first), then by CPT code for ties
                // NOTE: Bilateral adjustments were already applied during procedure creation
                const sortedFamilyProcs = family.procedures.sort((a, b) => {
                  if (b.allowable !== a.allowable) {
                    return b.allowable - a.allowable;
                  }
                  // Tiebreaker: alphabetical by CPT code
                  return a.cpt.localeCompare(b.cpt);
                });
                const baseProcedureAllowable = baseProcedureAllowables[family.baseCode];

                console.log(`\n👪 Processing family ${family.baseCode} (${sortedFamilyProcs.length} procedures, base allowable: $${baseProcedureAllowable.toFixed(2)})`);

                if (baseProcedureAllowable === 0) {
                  // Fallback to standard 100%/50% logic if base lookup failed
                  console.log(`⚠️ Using fallback 100%/50% logic for family ${family.baseCode}`);
                  sortedFamilyProcs.forEach((proc, index) => {
                    const reduction = index === 0 ? 1.0 : 0.5;
                    proc.mpprAllowable = proc.allowable * reduction;
                    console.log(`  ${proc.cpt} rank ${index + 1}: ${(reduction * 100).toFixed(0)}% → $${proc.mpprAllowable.toFixed(2)}`);
                  });
                } else {
                  // Apply proper base code subtraction method
                  sortedFamilyProcs.forEach((proc, index) => {
                    if (index === 0) {
                      // First (highest-valued) procedure is paid at 100%
                      proc.mpprAllowable = proc.allowable;
                      console.log(`  ${proc.cpt} rank 1 (primary): 100% → $${proc.mpprAllowable.toFixed(2)}`);
                    } else {
                      // Subsequent procedures: allowable - base_allowable
                      // For bilateral procedures with 150% logic, both amounts are already adjusted
                      let baseAllowableToSubtract = baseProcedureAllowable;

                      // If this procedure had bilateral 150% applied, subtract 150% of base
                      if (carrierBilateralSetting === 'option1' && proc.bilateral) {
                        baseAllowableToSubtract = baseProcedureAllowable * 1.5;
                        console.log(`  ${proc.cpt} rank ${index + 1}: $${proc.allowable.toFixed(2)} - $${baseAllowableToSubtract.toFixed(2)} (150% base) = $${Math.max(0, proc.allowable - baseAllowableToSubtract).toFixed(2)} [bilateral 150%]`);
                      } else {
                        console.log(`  ${proc.cpt} rank ${index + 1}: $${proc.allowable.toFixed(2)} - $${baseAllowableToSubtract.toFixed(2)} = $${Math.max(0, proc.allowable - baseAllowableToSubtract).toFixed(2)}`);
                      }

                      proc.mpprAllowable = Math.max(0, proc.allowable - baseAllowableToSubtract);
                    }
                  });
                }

                // Update family total for cross-family ranking
                family.totalAllowable = family.procedures.reduce((sum, proc) => sum + proc.mpprAllowable, 0);
                console.log(`  Family ${family.baseCode} total after base subtraction: $${family.totalAllowable.toFixed(2)}`);
              }
            });

            // Step 4: Process standalone procedures (apply 100% to them initially)
            standaloneEndos.forEach(proc => {
              proc.mpprAllowable = proc.allowable; // Start at 100%
            });

            // Step 5: Create units for cross-family ranking (families + standalones)
            const familyUnits = Object.values(endoscopyFamilies);
            const standaloneUnits = standaloneEndos.map(proc => ({
              baseCode: proc.cpt,
              procedures: [proc],
              totalAllowable: proc.allowable,
              familyRank: 0
            }));

            const allUnits = [...familyUnits, ...standaloneUnits];

            // Step 6: Sort all units by total allowable (descending) for cross-family MPPR, then by base code alphabetically
            console.log(`\n🏆 Cross-family ranking (${allUnits.length} units)...`);
            allUnits.sort((a, b) => {
              if (b.totalAllowable !== a.totalAllowable) {
                return b.totalAllowable - a.totalAllowable;
              }
              // Tiebreaker: alphabetical by base code
              return a.baseCode.localeCompare(b.baseCode);
            });

            // Assign ranks and log ranking
            allUnits.forEach((unit, index) => {
              unit.familyRank = index + 1;
            });

            console.log(`\n📊 Cross-Family Ranking:`);
            allUnits.forEach((unit, index) => {
              const unitType = unit.procedures.length > 1 ? 'Family' : 'Single';
              console.log(`  Rank ${index + 1}: ${unit.baseCode} (${unitType}) - Total: $${unit.totalAllowable.toFixed(2)}`);
            });

            // Step 7: Apply cross-family reductions (100%/50% across units)
            // NOTE: For enhanced endoscopy, base code subtraction within families IS the MPPR reduction
            // We only apply cross-family reductions to standalone procedures, not family procedures
            console.log(`\n🔄 Applying cross-family MPPR reductions (enhanced endoscopy mode)...`);
            allUnits.forEach((unit, unitIndex) => {
              const crossReduction = unitIndex === 0 ? 1.0 : 0.5;

              if (crossReduction !== 1.0) {
                // Only apply cross-family reduction to standalone procedures
                // Family procedures already had base code subtraction applied
                if (unit.procedures.length === 1 && !unit.procedures[0].endoBase) {
                  // This is a standalone procedure, apply cross-family reduction
                  // FIXED: Apply reduction to original allowable, not already-reduced mpprAllowable
                  unit.procedures.forEach(proc => {
                    proc.mpprAllowable = proc.allowable * crossReduction;
                    console.log(`  ${proc.cpt} (standalone): reduced to ${(crossReduction * 100).toFixed(0)}% = $${proc.mpprAllowable.toFixed(2)}`);
                  });
                } else {
                  console.log(`  Family ${unit.baseCode}: No cross-family reduction (base code subtraction already applied)`);
                }
              }
            });

            // Step 8: Handle base codes (set to $0 if child procedures are present)
            console.log(`\n🚫 Setting base codes to $0 where child procedures exist...`);
            const referencedBases = new Set<string>();
            Object.values(endoscopyFamilies).forEach(family => {
              if (family.procedures.length > 0) {
                referencedBases.add(family.baseCode);
                console.log(`  Base code referenced: ${family.baseCode}`);
              }
            });

            // Set any base codes that appear as procedures to $0
            allProcedures.forEach(proc => {
              if (referencedBases.has(proc.cpt)) {
                const originalAmount = proc.mpprAllowable;
                proc.mpprAllowable = 0;
                console.log(`  ${proc.cpt}: Set to $0 (was $${originalAmount.toFixed(2)}, has child procedures)`);
              }
            });

            console.log(`\n✅ Enhanced Endoscopy MPPR completed for ${procedures.length} procedures`);
          };

          // Call the async enhanced endoscopy processing function
          try {
            await processEnhancedEndoscopyMPPR();
          } catch (error) {
            console.error('❌ Error in enhanced endoscopy processing:', error);
            console.error('❌ Falling back to standard endoscopy logic');

            // Fallback to standard endoscopy logic if enhanced processing fails
            const sortedEndos = procedures
              .filter(proc => !(proc.isEMCode && hasCopayEnabled))
              .sort((a, b) => {
                // Sort by allowable (highest first)
                if (b.allowable !== a.allowable) {
                  return b.allowable - a.allowable;
                }
                // Tiebreaker: alphabetical by CPT code for consistent ordering
                return a.cpt.localeCompare(b.cpt);
              });

            sortedEndos.forEach((proc, index) => {
              const reduction = index === 0 ? 1.0 : 0.5;
              proc.mpprAllowable = proc.allowable * reduction;
              console.log(`🔄 Fallback Endo: ${proc.cpt} rank ${index + 1} = ${(reduction * 100).toFixed(0)}% → ${proc.mpprAllowable.toFixed(2)}`);
            });
          }
        } else {
          // Standard endoscopy logic (treat like indicator 2)
          console.log(`📊 Standard Endoscopy MPPR for Indicator 3...`);

          const sortedEndos = procedures
            .filter(proc => !(proc.isEMCode && hasCopayEnabled))
            .sort((a, b) => {
              // Sort by allowable (highest first)
              if (b.allowable !== a.allowable) {
                return b.allowable - a.allowable;
              }
              // Tiebreaker: alphabetical by CPT code for consistent ordering
              return a.cpt.localeCompare(b.cpt);
            });

          sortedEndos.forEach((proc, index) => {
            const reduction = index === 0 ? 1.0 : 0.5;
            proc.mpprAllowable = proc.allowable * reduction;
            console.log(`🔄 Standard Endo: ${proc.cpt} rank ${index + 1} = ${(reduction * 100).toFixed(0)}% → ${proc.mpprAllowable.toFixed(2)}`);
          });
        }
      }
    }

    // Apply cross-indicator reductions (100%/50% across different indicators)
    // FIXED: For enhanced endoscopy, treat families as single ranking units
    const allRankingUnits: Array<{
      indicator: string;
      procedure?: ExtendedProcedureResult;
      procedures?: ExtendedProcedureResult[];
      rankingAllowable: number;
      isRepresentative: boolean;
      isFamily?: boolean;
      familyBaseCode?: string;
    }> = [];

    Object.entries(indicatorGroups)
      .filter(([indicator]) => indicator !== '0' && indicator !== '9')
      .forEach(([indicator, procedures]) => {
        const filteredProcs = procedures.filter(proc => !(proc.isEMCode && hasCopayEnabled));

        if (indicator === '3' && carrierUsesEnhancedEndoscopy) {
          // FIXED: For enhanced endoscopy, group by families and treat families as single ranking units
          const familyGroups: {[baseCode: string]: ExtendedProcedureResult[]} = {};
          const standaloneProcedures: ExtendedProcedureResult[] = [];

          filteredProcs.forEach(proc => {
            if (proc.endoBase && String(proc.endoBase).trim() !== '') {
              const baseCode = String(proc.endoBase).trim();
              if (!familyGroups[baseCode]) familyGroups[baseCode] = [];
              familyGroups[baseCode].push(proc);
            } else {
              standaloneProcedures.push(proc);
            }
          });

          // Add family ranking units (family total as ranking allowable)
          Object.entries(familyGroups).forEach(([baseCode, familyProcs]) => {
            const familyTotal = familyProcs.reduce((sum, proc) => sum + proc.mpprAllowable, 0);
            allRankingUnits.push({
              indicator,
              procedures: familyProcs,
              rankingAllowable: familyTotal,
              isRepresentative: true,
              isFamily: true,
              familyBaseCode: baseCode
            });
          });

          // Add standalone endoscopy procedures
          standaloneProcedures.forEach(proc => {
            allRankingUnits.push({
              indicator,
              procedure: proc,
              rankingAllowable: proc.mpprAllowable,
              isRepresentative: true,
              isFamily: false
            });
          });

        } else {
          // Standard processing for non-enhanced endoscopy indicators
          // Group procedures by CPT code to handle bilateral logic
          const procsByCode = filteredProcs.reduce((groups: {[key: string]: ExtendedProcedureResult[]}, proc) => {
            if (!groups[proc.cpt]) groups[proc.cpt] = [];
            groups[proc.cpt].push(proc);
            return groups;
          }, {});

          Object.entries(procsByCode).forEach(([cptCode, procs]) => {
            if (procs.length === 1) {
              // Single procedure
              allRankingUnits.push({
                indicator,
                procedure: procs[0],
                rankingAllowable: procs[0].mpprAllowable,
                isRepresentative: true,
                isFamily: false
              });
            } else if (procs.length === 2 && procs[0].isBilateralSplit === true) {
              // Bilateral 100%/50% option - two separate ranking units
              // Use mpprAllowable which reflects within-indicator reductions
              procs.forEach(proc => {
                allRankingUnits.push({
                  indicator,
                  procedure: proc,
                  rankingAllowable: proc.mpprAllowable,
                  isRepresentative: true,
                  isFamily: false
                });
              });
            } else if (procs.length >= 2 && procs[0].isBilateralSplit !== true) {
              // Multiple units of same CPT - each ranks separately
              procs.forEach(proc => {
                allRankingUnits.push({
                  indicator,
                  procedure: proc,
                  rankingAllowable: proc.mpprAllowable,
                  isRepresentative: true,
                  isFamily: false
                });
              });
            }
          });
        }
      });

    // Sort by ranking allowable (highest first), then by CPT code alphabetically for ties
    // IMPORTANT: This sort must be deterministic to ensure consistent results regardless of input order
    allRankingUnits.sort((a, b) => {
      // First, sort by allowable (highest first)
      if (b.rankingAllowable !== a.rankingAllowable) {
        return b.rankingAllowable - a.rankingAllowable;
      }

      // If allowables are equal, use CPT code for stable sort
      const cptA = a.isFamily ? a.familyBaseCode! : a.procedure!.cpt;
      const cptB = b.isFamily ? b.familyBaseCode! : b.procedure!.cpt;

      // If CPT codes are different, sort alphabetically
      if (cptA !== cptB) {
        return cptA.localeCompare(cptB);
      }

      // If same CPT code and same allowable, we need another tiebreaker
      // For bilateral split procedures, prefer the one with higher mpprAllowable (the 100% one)
      if (!a.isFamily && !b.isFamily) {
        const allowableA = a.procedure!.mpprAllowable;
        const allowableB = b.procedure!.mpprAllowable;
        if (allowableB !== allowableA) {
          return allowableB - allowableA;
        }
      }

      // Final tiebreaker: maintain stable order (should rarely reach here)
      return 0;
    });

    if (allRankingUnits.length > 1) {
      console.log(`\n🔄 Applying cross-indicator 100%/50% reductions...`);
      console.log(`📊 Ranking units by allowable: ${allRankingUnits.map((unit, index) => {
        if (unit.isFamily) {
          return `${index + 1}. Family ${unit.familyBaseCode} (IND-${unit.indicator}) - $${unit.rankingAllowable.toFixed(2)}`;
        } else {
          return `${index + 1}. ${unit.procedure!.cpt} (IND-${unit.indicator}) - $${unit.rankingAllowable.toFixed(2)}`;
        }
      }).join(', ')}`);

      // Apply 100%/50% reductions based on ranking
      // FIXED: For enhanced endoscopy families, apply reduction to family total then distribute
      allRankingUnits.forEach((unit, rankIndex) => {
        const reductionFactor = rankIndex === 0 ? 1.0 : 0.5;

        if (unit.isFamily) {
          // FIXED: Enhanced endoscopy family - apply reduction to family total, then distribute proportionally
          const originalFamilyTotal = unit.rankingAllowable;
          const newFamilyTotal = originalFamilyTotal * reductionFactor;

          console.log(`🔄 Cross-indicator: Family ${unit.familyBaseCode} rank ${rankIndex + 1} reduced to ${(reductionFactor * 100).toFixed(0)}% = ${newFamilyTotal.toFixed(2)} (was ${originalFamilyTotal.toFixed(2)})`);

          // Distribute the reduction proportionally to each procedure in the family
          unit.procedures!.forEach(proc => {
            if (proc.mpprAllowable === 0) {
              // Skip base codes that are already $0
              console.log(`  ${proc.cpt}: Stays at $0 (base code)`);
              return;
            }

            const proportion = proc.mpprAllowable / originalFamilyTotal;
            const originalProcAmount = proc.mpprAllowable;
            proc.mpprAllowable = newFamilyTotal * proportion;

            console.log(`  ${proc.cpt}: ${originalProcAmount.toFixed(2)} → ${proc.mpprAllowable.toFixed(2)} (${(proportion * 100).toFixed(1)}% of family)`);
          });

        } else {
          // Individual procedure (non-family)
          const proc = unit.procedure!;

          // Skip base codes that were set to $0 by enhanced endoscopy
          if (proc.mpprAllowable === 0) {
            console.log(`🚫 Cross-indicator: ${proc.cpt} stays at $0 (base code with child procedures)`);
            return;
          }

          if (reductionFactor !== 1.0) {
            const originalAmount = proc.mpprAllowable;
            proc.mpprAllowable = proc.allowable * reductionFactor;

            const bilateralInfo = proc.bilateral ? ' (bilateral 150%)' : '';
            console.log(`🔄 Cross-indicator: ${proc.cpt}${bilateralInfo} rank ${rankIndex + 1} reduced to ${(reductionFactor * 100).toFixed(0)}% = ${proc.mpprAllowable.toFixed(2)} (was ${originalAmount.toFixed(2)})`);
          } else {
            const bilateralInfo = proc.bilateral ? ' (bilateral 150%)' : '';
            console.log(`✅ Cross-indicator: ${proc.cpt}${bilateralInfo} rank 1 stays at 100% = ${proc.mpprAllowable.toFixed(2)}`);
          }
        }
      });
    }

    console.log(`\n🎯 MPPR calculation completed using ${carrierUsesEnhancedEndoscopy ? 'enhanced' : 'standard'} endoscopy MPPR logic and ${carrierBilateralSetting} bilateral logic - 100%/50% rules applied throughout!`);
    
    // Debug: Show final procedure amounts
    console.log('\n📋 Final Procedure Summary:');
    allProcedures.forEach(proc => {
      const isBase = proc.endoBase && allProcedures.some(p => p.endoBase === proc.cpt && p.cpt !== proc.cpt);
      const bilateralInfo = proc.bilateral ? ' (BILATERAL)' : '';
      console.log(`  ${proc.cpt}: ${proc.charge.toFixed(2)} charge → ${proc.allowable.toFixed(2)} allowable → ${proc.mpprAllowable.toFixed(2)} final${isBase ? ' (ENDO BASE)' : ''}${bilateralInfo}`);
    });

    // Calculate totals
    const totalCharge = allProcedures.reduce((sum, proc) => sum + proc.charge, 0);
    const mpprAdjustedTotal = allProcedures.reduce((sum, proc) => sum + proc.mpprAllowable, 0);

    let deductibleDue = 0;
    let coinsuranceDue = 0;
    let copayDue = 0;
    let patientPayment = 0;
    let slidingFeePayment = totalCharge;
    let isCappedByOOP = false;

    if (calculatorData.insuranceCarrier !== 'None') {
      const deductibleRemaining = Number(calculatorData.deductibleRemaining);

      // For Medigap plans that don't use Out of Pocket (all Medicare except Plan K and L), use $999,999
      const shouldUseHighOOP = calculatorData.insuranceCarrier.toLowerCase().includes('medicare') &&
                               calculatorData.medigapPlan !== 'Plan K' &&
                               calculatorData.medigapPlan !== 'Plan L';
      const outOfPocketRemaining = shouldUseHighOOP ? 999999 : Number(calculatorData.outOfPocketRemaining);

      const coinsuranceRate = Number(calculatorData.coinsurance) / 100;

      // Separate E&M procedures (with copay) from other procedures
      const emProcedures = allProcedures.filter(proc => proc.isEMCode && hasCopayEnabled);
      const otherProcedures = allProcedures.filter(proc => !(proc.isEMCode && hasCopayEnabled));

      const emTotal = emProcedures.reduce((sum, proc) => sum + proc.mpprAllowable, 0);
      const otherTotal = otherProcedures.reduce((sum, proc) => sum + proc.mpprAllowable, 0);
      const totalAllowable = emTotal + otherTotal;

      console.log(`💰 Payment calculation - E&M Total: $${emTotal.toFixed(2)}, Other Total: $${otherTotal.toFixed(2)}, Deductible: $${deductibleRemaining}, Copay: $${copayAmountNum}`);

      // Apply deductible and copay precedence rules
      if (deductibleRemaining > 0) {
        if (deductibleRemaining >= copayAmountNum || !hasCopayEnabled) {
          // Deductible trumps copay - apply deductible to total allowable first
          deductibleDue = Math.min(totalAllowable, deductibleRemaining);
          const postDeductibleAmount = totalAllowable - deductibleDue;
          coinsuranceDue = postDeductibleAmount * coinsuranceRate;

          console.log(`📋 Deductible trumps copay: Deductible $${deductibleDue.toFixed(2)}, Post-deductible amount $${postDeductibleAmount.toFixed(2)}, Coinsurance $${coinsuranceDue.toFixed(2)}`);
        } else {
          // Remaining deductible < copay: apply deductible first, then copay/coinsurance logic
          deductibleDue = Math.min(totalAllowable, deductibleRemaining);
          const postDeductibleAmount = totalAllowable - deductibleDue;

          // For remaining amount, apply copay to E&M and coinsurance to others
          const postDeductibleEM = Math.max(0, emTotal - Math.max(0, deductibleRemaining - otherTotal));
          const postDeductibleOther = Math.max(0, otherTotal - Math.max(0, deductibleRemaining - emTotal));

          // E&M gets copay if there's remaining E&M amount after deductible
          copayDue = postDeductibleEM > 0 ? Math.min(postDeductibleEM, copayAmountNum) : 0;

          // Coinsurance applies to other procedures and any E&M amount exceeding copay
          const emCoinsuranceAmount = Math.max(0, postDeductibleEM - copayDue);
          const otherCoinsuranceAmount = postDeductibleOther;
          coinsuranceDue = (emCoinsuranceAmount + otherCoinsuranceAmount) * coinsuranceRate;

          console.log(`📋 Deductible < Copay: Deductible $${deductibleDue.toFixed(2)}, E&M Copay $${copayDue.toFixed(2)}, Coinsurance $${coinsuranceDue.toFixed(2)}`);
        }
      } else {
        // No deductible: copay applies to E&M, coinsurance to others
        if (hasCopayEnabled && emTotal > 0) {
          copayDue = Math.min(emTotal, copayAmountNum);
          const emCoinsuranceAmount = Math.max(0, emTotal - copayDue);
          coinsuranceDue = (emCoinsuranceAmount + otherTotal) * coinsuranceRate;

          console.log(`📋 No deductible: E&M Copay $${copayDue.toFixed(2)}, Coinsurance $${coinsuranceDue.toFixed(2)}`);
        } else {
          // No copay or no E&M codes: standard coinsurance on everything
          coinsuranceDue = totalAllowable * coinsuranceRate;
          console.log(`📋 No deductible, no copay: Standard coinsurance $${coinsuranceDue.toFixed(2)}`);
        }
      }

      const calculatedPatientPayment = deductibleDue + copayDue + coinsuranceDue;

      if (calculatedPatientPayment > outOfPocketRemaining) {
        patientPayment = outOfPocketRemaining;
        isCappedByOOP = true;
      } else {
        patientPayment = calculatedPatientPayment;
      }

      slidingFeePayment = 0;
    } else {
      // Self-pay calculation: First $150 no discount, anything over $150 gets 30% discount
      if (totalCharge <= 150) {
        slidingFeePayment = totalCharge;
      } else {
        const amountOver150 = totalCharge - 150;
        const discountedAmount = amountOver150 * 0.7; // 30% discount = pay 70%
        slidingFeePayment = 150 + discountedAmount;
      }
    }

    const calculationResults: Results = {
      procedures: allProcedures,
      totalCharge,
      mpprAdjustedTotal,
      deductibleDue,
      coinsuranceDue,
      copayDue,
      patientPayment,
      slidingFeePayment,
      isCappedByOOP,
      usedEnhancedEndoscopy: carrierUsesEnhancedEndoscopy,
      usedBilateralOption: carrierBilateralSetting // NEW: Track which bilateral option was used
    };

    setResults(calculationResults);

    // Save calculation if user is logged in
    if (currentUser) {
      try {
        console.log('💾 Attempting to save calculation for user:', currentUser.uid, currentUser.email);
        // Convert CPT codes back to legacy string format for storage
        const cptCodesString = calculatorData.cptCodes
          .filter(entry => entry.code.trim() !== '')
          .map(entry =>
            entry.bilateral ? `${entry.code.trim()} (bilateral)` : entry.code.trim()
          ).join(', ');
        const legacyCalculatorData = {
          ...calculatorData,
          cptCodes: cptCodesString
        };
        const calcId = await saveCalculation(currentUser.uid, currentUser.email || '', legacyCalculatorData, calculationResults);
        console.log('✅ Calculation saved successfully with ID:', calcId);
      } catch (error: any) {
        console.error('❌ Error saving calculation:', error);
        console.error('Error details:', error.code, error.message);
      }
    } else {
      console.log('⚠️ No user logged in - calculation not saved');
    }

    console.log(`🎉 Calculation completed with ${carrierUsesEnhancedEndoscopy ? 'enhanced' : 'standard'} endoscopy MPPR and ${carrierBilateralSetting} bilateral logic - 100%/50% rules applied throughout!`);
  };

  // Authentication functions
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError('');

    try {
      const result = await loginUser(email, password);
      // Auth state change handler will take care of redirecting
    } catch (error: any) {
      setAuthError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      setCalculatorData({
        patientId: '',
        cptCodes: [{ code: '', units: '1' }],
        insuranceCarrier: 'None',
        medigapPlan: 'None',
        deductibleRemaining: '',
        outOfPocketRemaining: '',
        coinsurance: '',
        hasCopay: 'No',
        copayAmount: ''
      });
      setResults(null);
      setFieldErrors({});
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Email verification functions
  const handleResendVerificationEmail = async () => {
    if (!pendingVerificationUser) return;
    
    setIsResendingEmail(true);
    setVerificationMessage('');

    try {
      await sendVerificationEmail(pendingVerificationUser);
      setVerificationMessage('Verification email sent! Please check your inbox.');
    } catch (error: any) {
      setVerificationMessage('Error sending verification email: ' + error.message);
    } finally {
      setIsResendingEmail(false);
    }
  };

  const handleCheckVerification = async () => {
    if (!pendingVerificationUser) return;
    
    try {
      await pendingVerificationUser.reload();
      
      if (checkEmailVerified(pendingVerificationUser)) {
        setVerificationMessage('Email verified successfully! Redirecting to app...');
      } else {
        setVerificationMessage('Email not verified yet. Please check your email and click the verification link.');
      }
    } catch (error: any) {
      setVerificationMessage('Error checking verification status: ' + error.message);
    }
  };

  const handleUseDifferentEmail = async () => {
    try {
      await logoutUser();
      setPendingVerificationUser(null);
      setVerificationMessage('');
      setAuthError('');
      setAppState('login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Password reset functions
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!resetEmail.trim()) {
      setResetMessage('Please enter your email address.');
      return;
    }

    try {
      await sendPasswordReset(resetEmail);
      setResetMessage('Password reset email sent! Please check your inbox.');
    } catch (error: any) {
      setResetMessage('Error sending reset email: ' + error.message);
    }
  };

  // Handle password confirmation and reset
  const handleConfirmPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordResetError('');

    // Validate passwords
    if (!newPassword || !confirmPassword) {
      setPasswordResetError('Please enter both password fields.');
      return;
    }

    // Validate against Firebase password policy
    if (newPassword.length < 8) {
      setPasswordResetError('Password must be at least 8 characters long.');
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setPasswordResetError('Password must contain at least one uppercase letter.');
      return;
    }

    if (!/[a-z]/.test(newPassword)) {
      setPasswordResetError('Password must contain at least one lowercase letter.');
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      setPasswordResetError('Password must contain at least one number.');
      return;
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      setPasswordResetError('Password must contain at least one special character (!@#$%^&* etc).');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordResetError('Passwords do not match. Please try again.');
      return;
    }

    try {
      setIsLoading(true);
      await confirmPasswordResetWithCode(resetCode, newPassword);
      setPasswordResetSuccess(true);

      // Auto-redirect to login after 2 seconds
      setTimeout(() => {
        setShowPasswordReset(false);
        setPasswordResetSuccess(false);
        setNewPassword('');
        setConfirmPassword('');
        setResetCode('');
        setResetMode(null);
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 2000);
    } catch (error: any) {
      setPasswordResetError(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // File upload handling
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress('Processing file...');

    try {
      let jsonData: any[] = [];

      if (file.name.toLowerCase().endsWith('.csv')) {
        const csvText = await file.text();
        jsonData = await parseCSVFile(csvText);
      } else {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        jsonData = XLSX.utils.sheet_to_json(firstSheet);
      }

      if (jsonData.length === 0) {
        alert('The file appears to be empty or invalid.');
        setIsUploading(false);
        return;
      }

      setUploadProgress('Uploading to database...');
      const uploadedSchedule = await uploadFeeScheduleWithCacheReset(
        file.name,
        jsonData,
        currentUser?.uid || ''
      );

      setFeeSchedule(uploadedSchedule);
      await loadInsuranceCarriers();
      
      alert(`Fee schedule "${uploadedSchedule.name}" uploaded successfully!`);
    } catch (error: any) {
      console.error('Upload error:', error);
      alert(`Error uploading file: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress('');
      event.target.value = '';
    }
  };

  // Handle insurance carrier change
  const handleInsuranceChange = (carrier: string): void => {
    const isMedicare = carrier.toLowerCase().includes('medicare');
    const isMedicaid = carrier.toLowerCase().includes('medicaid');

    let updatedData: CalculatorData = {
      patientId: calculatorData.patientId,
      cptCodes: calculatorData.cptCodes,
      insuranceCarrier: carrier,
      medigapPlan: isMedicare ? calculatorData.medigapPlan : 'None', // Reset medigap for non-Medicare carriers
      deductibleRemaining: isMedicaid ? '0' : '',
      outOfPocketRemaining: isMedicaid ? '0' : '',
      coinsurance: isMedicare ? '20' : (isMedicaid ? '0' : ''), // Set 20% for Medicare (default None plan)
      hasCopay: (carrier === 'None' || isMedicaid) ? 'No' : calculatorData.hasCopay,
      copayAmount: (carrier === 'None' || isMedicaid) ? '' : calculatorData.copayAmount
    };

    setCalculatorData(updatedData);
    setResults(null);
    setFieldErrors({}); // Clear all field errors when changing carrier
  };

  // Handle CPT code changes with bilateral support and modifier detection
  const handleCPTCodesChange = async (index: number, field: keyof CPTCodeEntry, value: string | boolean): Promise<void> => {
    const newCPTCodes = [...calculatorData.cptCodes];

    if (field === 'bilateral') {
      newCPTCodes[index] = { ...newCPTCodes[index], [field]: value as boolean };
    } else {
      newCPTCodes[index] = { ...newCPTCodes[index], [field]: value as string };
    }

    setCalculatorData({ ...calculatorData, cptCodes: newCPTCodes });
    setResults(null);

    if (fieldErrors.cptCodes) {
      setFieldErrors({ ...fieldErrors, cptCodes: false });
    }

    // If CPT code changed, check for available modifiers
    if (field === 'code' && typeof value === 'string' && value.trim()) {
      try {
        const modifiers = await getModifiersForCPT(value.trim());

        // Only show modifier dropdown if there are multiple entries (base + modifiers)
        if (modifiers.length > 1) {
          setCptModifiers(prev => ({ ...prev, [index]: modifiers }));
        } else {
          // Remove modifier dropdown if only one entry exists
          setCptModifiers(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });
          // Clear any selected modifier
          newCPTCodes[index] = { ...newCPTCodes[index], modifier: undefined };
          setCalculatorData({ ...calculatorData, cptCodes: newCPTCodes });
        }
      } catch (error) {
        console.error('Error fetching modifiers for CPT:', value, error);
      }
    }
  };

  const addCPTCodeField = (): void => {
    setCalculatorData({
      ...calculatorData,
      cptCodes: [...calculatorData.cptCodes, { code: '', units: '1' }]
    });
  };

  const removeCPTCodeField = (index: number): void => {
    if (calculatorData.cptCodes.length > 1) {
      const newCPTCodes = calculatorData.cptCodes.filter((_, i) => i !== index);
      setCalculatorData({ ...calculatorData, cptCodes: newCPTCodes });
      setResults(null);
    }
  };

  // Helper function to check if any bilateral procedures exist
  const hasBilateralProcedures = (results: Results): boolean => {
    return results.procedures.some(proc => proc.bilateral === true);
  };

  // Helper function to get procedure description from fee schedule
  const getProcedureDescription = (cptCode: string): string => {
    const metadata = cptMetadata[cptCode];
    const description = metadata?.description || 'Medical Procedure';
    // Escape HTML to prevent XSS from malicious CSV descriptions
    return escapeHtml(description);
  };

  // Helper function to format detailed procedure breakdown
  const formatProcedureBreakdown = (
    groupKey: string,  // Can be "cptCode" or "cptCode-modifier"
    procs: (ProcedureResult & { originalIndex: number })[],
    isBilateral: boolean,
    isBilateralSplit: boolean,
    results: Results
  ): string => {
    const firstProc = procs[0];
    // Extract base CPT code for description (remove modifier if present)
    const baseCptCode = firstProc.cpt;
    const description = getProcedureDescription(baseCptCode);
    // Display code includes modifier if present
    const displayCode = groupKey;

    if (isBilateral || isBilateralSplit) {
      // Bilateral procedure breakdown - grouped format
      const totalCharge = isBilateralSplit ? procs.reduce((sum, p) => sum + p.charge, 0) : firstProc.charge;
      const singleSideCharge = totalCharge / 2;
      const totalAllowable = isBilateralSplit ? procs.reduce((sum, p) => sum + p.allowable, 0) : firstProc.allowable;
      const singleSideAllowable = isBilateral ? firstProc.allowable / 2 : firstProc.allowable;
      const finalAmount = isBilateralSplit ? procs.reduce((sum, p) => sum + p.mpprAllowable, 0) : firstProc.mpprAllowable;

      let breakdown = `<strong>${description} - Bilateral (CPT ${displayCode})</strong>`;

      if (results.usedBilateralOption === 'option1') {
        // 150% Option - treated as single line item by insurance
        const originalSingleSideAllowable = totalAllowable / 1.5;
        // Reset breakdown to not use bilateral grouping for 150% option
        breakdown = `<strong>${description} - Bilateral (CPT ${displayCode})</strong>`;
        // Check if this procedure was reduced (has savings)
        const savings = totalAllowable - finalAmount;
        if (savings > 0) {
          breakdown += `\n  • Your insurance reduces the allowable for related procedures.`;
        } else {
          breakdown += `\n  • Your insurance treats bilateral procedures as a single line item at 150% allowable.`;
        }
        breakdown += `\n  • Charge: $${totalCharge.toFixed(2)} (bilateral)`;
        breakdown += `\n  • Insurance Approved Amount: $${finalAmount.toFixed(2)}`;
        breakdown += `\n  • <strong>Final Amount: $${finalAmount.toFixed(2)}</strong>`;
      } else {
        // 100%/50% Option - treated as separate units
        const firstSideAmount = isBilateralSplit ? procs[0].mpprAllowable : finalAmount * 0.67;
        const secondSideAmount = isBilateralSplit ? (procs[1]?.mpprAllowable || 0) : finalAmount * 0.33;

        // Check if both sides are actually getting the same reduced amount (both at 50% due to MPPR ranking)
        const bothSidesReduced = Math.abs(firstSideAmount - secondSideAmount) < 0.01; // Within 1 cent
        const originalSingleSideAllowable = totalAllowable / 2; // Original allowable per side before bilateral logic
        const isFirstSideReduced = firstSideAmount < (originalSingleSideAllowable * 0.99); // 99% threshold to account for rounding

        // Check if this is enhanced endoscopy (has endoBase)
        const isEnhancedEndo = (firstProc as any).endoBase && results.usedEnhancedEndoscopy;

        if (bothSidesReduced && isFirstSideReduced) {
          if (isEnhancedEndo) {
            breakdown += `\n  • Both sides reduced due to related procedures.`;
            breakdown += `\n    • First Side: Charge $${singleSideCharge.toFixed(2)} → Approved $${firstSideAmount.toFixed(2)} (reduced %)`;
            breakdown += `\n    • Second Side: Charge $${singleSideCharge.toFixed(2)} → Approved $${secondSideAmount.toFixed(2)} (reduced %)`;
          } else {
            breakdown += `\n  • Both sides reduced to 50% due to higher-priority procedures.`;
            breakdown += `\n    • First Side: Charge $${singleSideCharge.toFixed(2)} → Approved $${firstSideAmount.toFixed(2)} (50%)`;
            breakdown += `\n    • Second Side: Charge $${singleSideCharge.toFixed(2)} → Approved $${secondSideAmount.toFixed(2)} (50%)`;
          }
        } else {
          if (isEnhancedEndo) {
            breakdown += `\n  • Your insurance typically pays 100% of the first side and the second side is reduced.`;
            breakdown += `\n    • First Side: Charge $${singleSideCharge.toFixed(2)} → Approved $${firstSideAmount.toFixed(2)} (100%)`;
            breakdown += `\n    • Second Side: Charge $${singleSideCharge.toFixed(2)} → Approved $${secondSideAmount.toFixed(2)} (reduced %)`;
          } else {
            breakdown += `\n  • Your insurance typically pays 100% of the first side and 50% of the second side.`;
            breakdown += `\n    • First Side: Charge $${singleSideCharge.toFixed(2)} → Approved $${firstSideAmount.toFixed(2)} (100%)`;
            breakdown += `\n    • Second Side: Charge $${singleSideCharge.toFixed(2)} → Approved $${secondSideAmount.toFixed(2)} (50%)`;
          }
        }
        breakdown += `\n  • <strong>Final Amount: $${finalAmount.toFixed(2)}</strong>`;
      }

      return breakdown;

    }

    let breakdown = `<strong>${description} (CPT ${displayCode})</strong>`;

    if (procs.length > 1) {
      // Multiple units breakdown
      const totalCharge = procs.reduce((sum, p) => sum + p.charge, 0);
      const perUnitCharge = totalCharge / procs.length;
      const totalAllowable = procs.reduce((sum, p) => sum + p.allowable, 0);
      const perUnitAllowable = totalAllowable / procs.length;
      const finalAmount = procs.reduce((sum, p) => sum + p.mpprAllowable, 0);

      breakdown += `\n  • Additional procedures are reduced to 50% of the approved amount.`;
      breakdown += `\n  • Charge: $${perUnitCharge.toFixed(2)} × ${procs.length} units = $${totalCharge.toFixed(2)}`;
      breakdown += `\n  • Insurance Approved Amount: $${perUnitAllowable.toFixed(2)} per unit`;
      breakdown += `\n  • <strong>Final Amount: $${finalAmount.toFixed(2)}</strong>`;

    } else {
      // Single procedure breakdown
      const savings = firstProc.allowable - firstProc.mpprAllowable;
      const hasCopayEnabled = calculatorData.hasCopay === 'Yes' && calculatorData.copayAmount.trim() !== '';
      const copayAmountNum = hasCopayEnabled ? Number(calculatorData.copayAmount) : 0;
      const isEMWithCopay = isEMCode(baseCptCode) && hasCopayEnabled && results.copayDue > 0;

      if (isEMWithCopay) {
        // Office visit with copay
        breakdown += `\n  • Office visits use your fixed copay amount.`;
        breakdown += `\n  • No deductible or coinsurance applies.`;
        breakdown += `\n  • Charge: $${firstProc.charge.toFixed(2)}`;
        breakdown += `\n  • Insurance Approved Amount: $${firstProc.allowable.toFixed(2)}`;
        breakdown += `\n  • <strong>Final Amount: $${copayAmountNum.toFixed(2)}</strong>`;
      } else {
        // Regular procedure logic
        // Check if this is an EM code with copay waived due to deductible
        const isEMWithCopayWaived = isEMCode(baseCptCode) && hasCopayEnabled && results.copayDue === 0 && results.deductibleDue > 0;

        if (isEMWithCopayWaived) {
          breakdown += `\n  • Copay waived, your deductible and coinsurance both apply.`;
        } else if (savings > 0) {
          // Check if this is enhanced endoscopy reduction (related) or standard MPPR (additional)
          const procWithEndoBase = firstProc as any;
          const isEnhancedEndoscopy = procWithEndoBase.endoBase && procWithEndoBase.endoBase !== baseCptCode;
          if (isEnhancedEndoscopy) {
            breakdown += `\n  • Your insurance reduces the allowable for related procedures.`;
          } else {
            breakdown += `\n  • Your insurance reduces the allowable for additional procedures.`;
          }
        } else {
          breakdown += `\n  • This is the primary procedure, so it receives the full approved amount.`;
        }
        breakdown += `\n  • Charge: $${firstProc.charge.toFixed(2)}`;
        breakdown += `\n  • Insurance Approved Amount: $${firstProc.allowable.toFixed(2)}`;
        breakdown += `\n  • <strong>Final Amount: $${firstProc.mpprAllowable.toFixed(2)}</strong>`;
      }
    }

    return breakdown;
  };

  // Helper function to add copay explanation (only when copay doesn't apply due to deductible)
  const getCopayExplanation = (results: Results): string => {
    // Copay explanation is now shown inline with the procedure breakdown
    // No need to add it at the bottom anymore
    return '';
  };

  // Helper function to generate procedure details
  const generateProcedureDetails = (results: Results): string => {
    const groupedProcedures = results.procedures.reduce((acc: { [key: string]: (ProcedureResult & { originalIndex: number })[] }, proc, index) => {
      // Group by CPT + modifier combination
      const groupKey = proc.modifier ? `${proc.cpt}-${proc.modifier}` : proc.cpt;
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push({ ...proc, originalIndex: index });
      return acc;
    }, {});

    // Check if this is a self-pay calculation
    const isSelfPay = calculatorData.insuranceCarrier === 'None';

    if (isSelfPay) {
      // Self-pay display - only show charges with brief explanation
      // Sort by charge amount (highest to lowest) since self-pay uses charges
      const sortedEntries = Object.entries(groupedProcedures).sort(([, procsA], [, procsB]) => {
        const totalChargeA = procsA.reduce((sum, p) => sum + p.charge, 0);
        const totalChargeB = procsB.reduce((sum, p) => sum + p.charge, 0);
        return totalChargeB - totalChargeA;
      });

      const procedureList = sortedEntries.map(([groupKey, procs]) => {
        const firstProc = procs[0];
        const isBilateral = firstProc.bilateral || false;
        const isBilateralSplit = (firstProc as any).isBilateralSplit || false;
        // Extract base CPT code for description (remove modifier if present)
        const baseCptCode = firstProc.cpt;
        const description = getProcedureDescription(baseCptCode);
        // Display code includes modifier if present
        const displayCode = groupKey;

        if (procs.length === 1 && !isBilateral) {
          return `${description} (CPT ${displayCode})     • Our Charge: $${firstProc.charge.toFixed(2)}
     • Your Cost (Self-Pay): $${results.slidingFeePayment > 0 ? (firstProc.charge * (results.slidingFeePayment / results.totalCharge)).toFixed(2) : firstProc.charge.toFixed(2)}`;
        } else if (isBilateral || isBilateralSplit) {
          const perSideCharge = isBilateral ? (firstProc.charge / 2) : (firstProc.charge);
          const totalCharge = isBilateralSplit ? procs.reduce((sum, p) => sum + p.charge, 0) : firstProc.charge;
          const yourCost = results.slidingFeePayment > 0 ? (totalCharge * (results.slidingFeePayment / results.totalCharge)).toFixed(2) : totalCharge.toFixed(2);
          const yourCostPerSide = (parseFloat(yourCost) / 2).toFixed(2);
          return `${description} - Bilateral (CPT ${displayCode})
         • Left Side: Our Charge $${perSideCharge.toFixed(2)} → Your Cost $${yourCostPerSide}
         • Right Side: Our Charge $${perSideCharge.toFixed(2)} → Your Cost $${yourCostPerSide}
         • Total Cost (Self-Pay): $${yourCost}`;
        } else {
          const totalCharge = procs.reduce((sum, p) => sum + p.charge, 0);
          const perUnitCharge = totalCharge / procs.length;
          const yourCost = results.slidingFeePayment > 0 ? (totalCharge * (results.slidingFeePayment / results.totalCharge)).toFixed(2) : totalCharge.toFixed(2);
          return `${description} (CPT ${displayCode})     • Our Charge: $${perUnitCharge.toFixed(2)} per unit × ${procs.length} units = $${totalCharge.toFixed(2)}
     • Your Cost (Self-Pay): $${yourCost}`;
        }
      }).join('\n\n');

      return `${procedureList}

SELF-PAY DISCOUNT: 30% discount applied to charges over $150${getCopayExplanation(results)}`;
    } else {
      // Insurance display - group endoscopy families when enhanced endoscopy is used
      if (results.usedEnhancedEndoscopy) {
        // Group procedures by endoscopy families
        const endoscopyFamilies: { [baseCode: string]: (ProcedureResult & { originalIndex: number })[] } = {};
        const standaloneProcs: (ProcedureResult & { originalIndex: number })[] = [];

        Object.entries(groupedProcedures).forEach(([cptCode, procs]) => {
          procs.forEach(proc => {
            const extProc = proc as any; // Type assertion to access endoBase
            if (extProc.endoBase && String(extProc.endoBase).trim() !== '') {
              const baseCode = String(extProc.endoBase).trim();
              if (!endoscopyFamilies[baseCode]) {
                endoscopyFamilies[baseCode] = [];
              }
              endoscopyFamilies[baseCode].push(proc);
            } else {
              standaloneProcs.push(proc);
            }
          });
        });

        const displaySections: string[] = [];

        // Display endoscopy families
        if (Object.keys(endoscopyFamilies).length > 0) {
          Object.entries(endoscopyFamilies).forEach(([baseCode, familyProcs]) => {
            const familyCharge = familyProcs.reduce((sum, p) => sum + p.charge, 0);
            const familyAllowed = familyProcs.reduce((sum, p) => sum + p.mpprAllowable, 0);

            displaySections.push(`\nENDOSCOPY PROCEDURES:`);

            // Group family procedures by CPT code + modifier to avoid duplicates
            const familyProcsByCpt: { [key: string]: (ProcedureResult & { originalIndex: number })[] } = {};
            familyProcs.forEach(proc => {
              const groupKey = proc.modifier ? `${proc.cpt}-${proc.modifier}` : proc.cpt;
              if (!familyProcsByCpt[groupKey]) {
                familyProcsByCpt[groupKey] = [];
              }
              familyProcsByCpt[groupKey].push(proc);
            });

            // Sort by total allowable per CPT (highest to lowest), then alphabetically
            const sortedFamilyCpts = Object.entries(familyProcsByCpt).sort(([cptA, procsA], [cptB, procsB]) => {
              const totalA = procsA.reduce((sum, p) => sum + p.allowable, 0);
              const totalB = procsB.reduce((sum, p) => sum + p.allowable, 0);
              if (totalB !== totalA) {
                return totalB - totalA;
              }
              // Tiebreaker: alphabetical
              return cptA.localeCompare(cptB);
            });

            sortedFamilyCpts.forEach(([cptCode, procGroup]) => {
              const firstProc = procGroup[0];
              const isBilateral = firstProc.bilateral || false;
              const isBilateralSplit = (firstProc as any).isBilateralSplit || false;

              displaySections.push(formatProcedureBreakdown(cptCode, procGroup, isBilateral, isBilateralSplit, results));
            });

            displaySections.push(`\nENDOSCOPY FAMILY TOTAL: $${familyAllowed.toFixed(2)}`);
          });
        }

        // Display standalone procedures
        if (standaloneProcs.length > 0) {
          displaySections.push(`\nOTHER PROCEDURES:`);

          // Group standalone procedures by CPT code + modifier
          const standaloneGrouped = standaloneProcs.reduce((acc: { [key: string]: (ProcedureResult & { originalIndex: number })[] }, proc) => {
            const groupKey = proc.modifier ? `${proc.cpt}-${proc.modifier}` : proc.cpt;
            if (!acc[groupKey]) {
              acc[groupKey] = [];
            }
            acc[groupKey].push(proc);
            return acc;
          }, {});

          // Sort standalone procedures by allowable amount (highest to lowest)
          const sortedStandaloneEntries = Object.entries(standaloneGrouped).sort(([, procsA], [, procsB]) => {
            const allowableA = procsA.reduce((sum, p) => sum + p.allowable, 0);
            const allowableB = procsB.reduce((sum, p) => sum + p.allowable, 0);
            return allowableB - allowableA;
          });

          sortedStandaloneEntries.forEach(([cptCode, procs]) => {
            const firstProc = procs[0];
            const isBilateral = firstProc.bilateral || false;
            const isBilateralSplit = (firstProc as any).isBilateralSplit || false;

            displaySections.push(formatProcedureBreakdown(cptCode, procs, isBilateral, isBilateralSplit, results));
          });
        }

        return `${displaySections.join('\n').trim()}${getCopayExplanation(results)}`;
      } else {
        // Standard display - show full details with per-unit information
        // Sort procedures by allowable amount (highest to lowest)
        const sortedEntries = Object.entries(groupedProcedures).sort(([, procsA], [, procsB]) => {
          const allowableA = procsA.reduce((sum, p) => sum + p.allowable, 0);
          const allowableB = procsB.reduce((sum, p) => sum + p.allowable, 0);
          return allowableB - allowableA;
        });

        return sortedEntries.map(([cptCode, procs]) => {
          const firstProc = procs[0];
          const isBilateral = firstProc.bilateral || false;
          const isBilateralSplit = (firstProc as any).isBilateralSplit || false;

          return formatProcedureBreakdown(cptCode, procs, isBilateral, isBilateralSplit, results);
        }).join('\n\n') + getCopayExplanation(results);
      }
    }
  };

  // Modern print functionality with enhanced styling and no new tab
  const handlePrint = (): void => {
    if (!results) return;

    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();

    // Generate CPT codes string for display with bilateral info
    const cptCodesString = calculatorData.cptCodes
      .filter(entry => entry.code.trim() !== '')
      .map(entry => {
        let display = entry.code.trim();
        if (entry.bilateral) {
          display += ' (Bilateral)';
        } else if (entry.units.trim() !== '1') {
          display += ` (${entry.units} units)`;
        }
        return display;
      })
      .join(', ');

    const procedureDetails = generateProcedureDetails(results);

    // Create a modern print content with better HTML structure
    const printHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Good Faith Estimate Calculator Results</title>
    <style>
        @media print {
            @page {
                margin: 0.75in;
                size: letter;
            }
            body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
        }

        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.4;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 15px;
            background: white;
            font-size: 12px;
        }

        .header {
            text-align: center;
            border-bottom: 2px solid #1d87b9;
            padding-bottom: 15px;
            margin-bottom: 20px;
        }

        .header h1 {
            color: #1d87b9;
            margin: 0;
            font-size: 22px;
            font-weight: 600;
        }

        .header .subtitle {
            color: #666;
            font-size: 11px;
            margin-top: 5px;
        }

        .section {
            margin-bottom: 15px;
            padding: 12px;
            border-left: 3px solid #1d87b9;
            border-top: 1px solid #e2e8f0;
            border-right: 1px solid #e2e8f0;
            border-bottom: 1px solid #e2e8f0;
        }

        .section h2 {
            color: #1d87b9;
            margin: 0 0 10px 0;
            font-size: 14px;
            font-weight: 600;
        }

        .info-grid {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: 4px 15px;
            margin-bottom: 8px;
        }

        .two-column-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 15px;
            padding: 12px;
            border-left: 3px solid #1d87b9;
            border-top: 1px solid #e2e8f0;
            border-right: 1px solid #e2e8f0;
            border-bottom: 1px solid #e2e8f0;
        }

        .column-content h2 {
            color: #1d87b9;
            margin: 0 0 10px 0;
            font-size: 14px;
            font-weight: 600;
        }

        .label {
            font-weight: 600;
            color: #555;
            font-size: 11px;
        }

        .value {
            color: #333;
            font-size: 11px;
        }

        .procedure-details {
            white-space: pre-line;
            line-height: 1.4;
            font-size: 11px;
        }

        .summary {
            border-left-color: #10b981;
            background: #f8fffe;
        }

        .summary .total-amount {
            font-size: 16px;
            font-weight: bold;
            color: #059669;
            text-align: center;
            margin: 10px 0;
            padding: 10px;
            background: white;
            border-radius: 4px;
            border: 1px solid #10b981;
        }

        .patient-responsibility {
            border-left-color: #f59e0b;
            background: #fffef7;
        }

        .disclaimer {
            margin-top: 15px;
            padding: 10px;
            background: #f1f5f9;
            border-radius: 4px;
            font-size: 10px;
            color: #64748b;
            text-align: center;
            line-height: 1.3;
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>Good Faith Estimate Calculator Results</h1>
        <div class="subtitle">Generated: ${currentDate} at ${currentTime}</div>
    </div>

    <div class="two-column-section">
        <div class="column-content">
            <h2>Patient Information</h2>
            <div class="info-grid">
                <span class="label">Patient ID:</span>
                <span class="value">___________________________</span>
                <span class="label">CPT Codes:</span>
                <span class="value">${cptCodesString}</span>
                <span class="label">Insurance:</span>
                <span class="value">${calculatorData.insuranceCarrier}</span>
                ${calculatorData.insuranceCarrier.toLowerCase().includes('medicare') && calculatorData.medigapPlan !== 'None' ? `
                <span class="label">Medigap:</span>
                <span class="value">${calculatorData.medigapPlan}</span>` : ''}
            </div>
        </div>
        ${calculatorData.insuranceCarrier !== 'None' ? `
        <div class="column-content">
            <h2>Insurance Details</h2>
            <div class="info-grid">
                <span class="label">Deductible Remaining:</span>
                <span class="value">$${calculatorData.deductibleRemaining}</span>
                <span class="label">Out of Pocket Remaining:</span>
                <span class="value">$${calculatorData.outOfPocketRemaining}</span>
                <span class="label">Coinsurance:</span>
                <span class="value">${calculatorData.coinsurance}%</span>
                ${calculatorData.hasCopay === 'Yes' ? `
                <span class="label">Copay Amount:</span>
                <span class="value">$${calculatorData.copayAmount}</span>` : ''}
            </div>
        </div>` : '<div class="column-content"></div>'}
    </div>

    <div class="section">
        <h2>Procedure Details</h2>
        <div class="procedure-details">${procedureDetails}</div>
    </div>

    <div class="section summary">
        <h2>Summary</h2>
        <div class="info-grid">
            <span class="label">Total Charge:</span>
            <span class="value">$${results.totalCharge.toFixed(2)}</span>
            <span class="label">Insurance Approved Amount:</span>
            <span class="value">$${results.mpprAdjustedTotal.toFixed(2)}</span>
            ${hasBilateralProcedures(results) && results.usedBilateralOption ? `
            <span class="label">Bilateral Logic:</span>
            <span class="value">${results.usedBilateralOption === 'option1' ? '150% Option' : '100%/50% Option'}</span>` : ''}
        </div>
    </div>

    ${calculatorData.insuranceCarrier !== 'None' ? `
    <div class="section patient-responsibility">
        <h2>Patient Responsibility</h2>
        <div class="info-grid">
            <span class="label">Deductible Due:</span>
            <span class="value">$${results.deductibleDue.toFixed(2)}</span>
            ${results.copayDue > 0 ? `
            <span class="label">Copay Due:</span>
            <span class="value">$${results.copayDue.toFixed(2)}</span>` : ''}
            <span class="label">Your Remaining Share (${calculatorData.coinsurance}%):</span>
            <span class="value">$${results.coinsuranceDue.toFixed(2)}</span>
        </div>
        <div class="total-amount">
            Total Patient Payment: $${results.patientPayment.toFixed(2)}${results.isCappedByOOP ? ' (Capped by Out-of-Pocket Maximum)' : ''}
        </div>
    </div>` : `
    <div class="section patient-responsibility">
        <h2>Self-Pay Amount</h2>
        <div class="total-amount">
            Self-Pay Amount: $${results.slidingFeePayment.toFixed(2)}
        </div>
    </div>`}

    <div class="disclaimer">
        This is a good faith estimate of your expected charges. Actual charges may vary based on additional services, complications, or changes in your treatment plan.
    </div>
</body>
</html>`;

    // Create a hidden iframe for printing (modern approach)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);

    if (printFrame.contentWindow) {
      printFrame.contentWindow.document.open();
      printFrame.contentWindow.document.write(printHtml);
      printFrame.contentWindow.document.close();

      // Wait for content to load then print
      setTimeout(() => {
        if (printFrame.contentWindow) {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();

          // Clean up the iframe after printing
          setTimeout(() => {
            document.body.removeChild(printFrame);
          }, 100);
        }
      }, 500);
    }
  };

  // Render loading state
  if (appState === 'loading') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        fontFamily: modernFontFamily
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p style={{ color: '#6b7280', fontSize: '16px' }}>Loading Good Faith Estimate Calculator...</p>
        </div>
      </div>
    );
  }

  // Render verification screen
  if (appState === 'verification') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        fontFamily: modernFontFamily
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: '500px'
        }}>
          <h2 style={{ textAlign: 'center', marginBottom: '24px', color: '#1f2937' }}>
            Verify Your Email
          </h2>
          
          <p style={{ textAlign: 'center', marginBottom: '24px', color: '#6b7280' }}>
            We've sent a verification email to <strong>{pendingVerificationUser?.email}</strong>. 
            Please check your inbox and click the verification link to continue.
          </p>

          {verificationMessage && (
            <div style={{ 
              padding: '12px', 
              marginBottom: '20px', 
              backgroundColor: verificationMessage.includes('Error') ? '#fef2f2' : '#f0f9ff',
              border: `1px solid ${verificationMessage.includes('Error') ? '#fecaca' : '#bfdbfe'}`,
              borderRadius: '6px',
              color: verificationMessage.includes('Error') ? '#dc2626' : '#1d4ed8'
            }}>
              {verificationMessage}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <button
              onClick={handleResendVerificationEmail}
              disabled={isResendingEmail}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: isResendingEmail ? '#9ca3af' : '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '500',
                cursor: isResendingEmail ? 'not-allowed' : 'pointer'
              }}
            >
              {isResendingEmail ? 'Sending...' : 'Resend Email'}
            </button>
            
            <button
              onClick={handleCheckVerification}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              I've Verified
            </button>
          </div>

          <button
            onClick={handleUseDifferentEmail}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: 'transparent',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Use Different Email
          </button>
        </div>
      </div>
    );
  }

  // Render login screen
  if (appState === 'login') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        fontFamily: modernFontFamily
      }}>
        <div style={{
          backgroundColor: 'white', 
          padding: '40px', 
          borderRadius: '12px', 
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: '400px'
        }}>
          <h2 style={{ textAlign: 'center', marginBottom: '32px', color: '#1d87b9' }}>
            Good Faith Estimate Calculator
          </h2>

          {showPasswordReset ? (
            // Password Reset/Activation Form
            <div>
              {passwordResetSuccess ? (
                <div style={{
                  padding: '24px',
                  backgroundColor: '#d1fae5',
                  border: '1px solid #6ee7b7',
                  borderRadius: '6px',
                  color: '#065f46',
                  textAlign: 'center'
                }}>
                  <h3 style={{ marginBottom: '12px' }}>Password Changed Successfully!</h3>
                  <p>You can now sign in with your new password.</p>
                  <p style={{ marginTop: '8px', fontSize: '14px' }}>Redirecting to login...</p>
                </div>
              ) : (
                <form onSubmit={handleConfirmPasswordReset}>
                  <div style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#1e40af'
                  }}>
                    <strong>Password Requirements:</strong>
                    <ul style={{ margin: '8px 0 0 20px', paddingLeft: '0' }}>
                      <li>At least 8 characters long</li>
                      <li>One uppercase letter (A-Z)</li>
                      <li>One lowercase letter (a-z)</li>
                      <li>One number (0-9)</li>
                      <li>One special character (!@#$%^&* etc)</li>
                    </ul>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                      New Password:
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '16px'
                      }}
                      placeholder="Enter your password"
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                      Confirm Password:
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '16px'
                      }}
                      placeholder="Re-enter your password"
                    />
                  </div>

                  {passwordResetError && (
                    <div style={{
                      marginBottom: '20px',
                      padding: '12px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      color: '#dc2626'
                    }}>
                      {passwordResetError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: isLoading ? '#9ca3af' : '#1d87b9',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontFamily: modernFontFamily
                    }}
                  >
                    {isLoading ? 'Setting Password...' : 'Set Password'}
                  </button>
                </form>
              )}
            </div>
          ) : !showForgotPassword ? (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                  Email:
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '16px'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                  Password:
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '16px'
                  }}
                />
              </div>
              
              {authError && (
                <div style={{ 
                  marginBottom: '20px', 
                  padding: '12px', 
                  backgroundColor: '#fef2f2', 
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#dc2626'
                }}>
                  {authError}
                </div>
              )}
              
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '288px',
                  height: '64px',
                  border: isLoading ? '3px solid #9ca3af' : '3px solid #1d87b9',
                  borderRadius: '35px',
                  transition: 'all 0.3s',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  background: isLoading ? '#9ca3af' : '#1d87b9',
                  fontSize: '1.1em',
                  fontWeight: '550',
                  color: 'white',
                  fontFamily: modernFontFamily,
                  margin: '0 auto 16px auto',
                  display: 'block'
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.color = '#1d87b9';
                    e.currentTarget.style.fontSize = '1.3em';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = '#1d87b9';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.fontSize = '1.1em';
                  }
                }}
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </button>

              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Forgot Password?
              </button>
            </form>
          ) : (
            <form onSubmit={handlePasswordReset}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                  Enter your email to reset password:
                </label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '16px'
                  }}
                />
              </div>

              {resetMessage && (
                <div style={{ 
                  marginBottom: '20px', 
                  padding: '12px', 
                  backgroundColor: resetMessage.includes('Error') ? '#fef2f2' : '#f0f9ff',
                  border: `1px solid ${resetMessage.includes('Error') ? '#fecaca' : '#bfdbfe'}`,
                  borderRadius: '6px',
                  color: resetMessage.includes('Error') ? '#dc2626' : '#1d4ed8'
                }}>
                  {resetMessage}
                </div>
              )}

              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  marginBottom: '16px'
                }}
              >
                Send Reset Email
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetMessage('');
                  setResetEmail('');
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Back to Login
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Main calculator interface
  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px',
      backgroundColor: '#f9fafb',
      minHeight: '100vh',
      fontFamily: modernFontFamily
    }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, color: '#1d87b9', fontSize: '28px', fontWeight: 'bold' }}>
            Good Faith Estimate Calculator
          </h1>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
          <button
          onClick={handleLogout}
          style={{
            width: '120px',
            height: '30px',
            border: '3px solid lightcoral',
            borderRadius: '22px',
            transition: 'all 0.3s',
            cursor: 'pointer',
            background: 'white',
            fontSize: '0.8em',
            fontWeight: '550',
            color: 'lightcoral',
            fontFamily: modernFontFamily
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'lightcoral';
            e.currentTarget.style.color = 'white';
            e.currentTarget.style.fontSize = '0.9em';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.color = 'lightcoral';
            e.currentTarget.style.fontSize = '0.8em';
          }}
        >
          Logout
        </button>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '12px', textAlign: 'right' }}>
          User: {currentUser?.email} | Role: {userProfile?.role}
        </p>
        </div>
      </div>

      {/* Admin Panel - Only show for admins */}
      {userProfile?.role === 'admin' && (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
          marginBottom: '24px',
          border: '2px solid #1d87b9'
        }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: adminPanelOpen ? '20px' : '0' }}>
            <button
              onClick={(e) => {
                const button = e.currentTarget;
                const newState = !adminPanelOpen;
                setAdminPanelOpen(newState);
                // Force immediate style update after click
                const color = newState ? 'lightcoral' : '#1d87b9';
                button.style.background = color;
                button.style.borderColor = color;
                button.style.color = 'white';
              }}
              style={{
                width: '166px',
                height: '32px',
                border: adminPanelOpen ? '3px solid lightcoral' : '3px solid #1d87b9',
                borderRadius: '18px',
                transition: 'all 0.3s',
                cursor: 'pointer',
                background: 'white',
                fontSize: '0.7em',
                fontWeight: '550',
                color: adminPanelOpen ? 'lightcoral' : '#1d87b9',
                fontFamily: modernFontFamily
              }}
              onMouseEnter={(e) => {
                const color = adminPanelOpen ? 'lightcoral' : '#1d87b9';
                e.currentTarget.style.background = color;
                e.currentTarget.style.borderColor = color;
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.fontSize = '0.8em';
              }}
              onMouseLeave={(e) => {
                const color = adminPanelOpen ? 'lightcoral' : '#1d87b9';
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = color;
                e.currentTarget.style.color = color;
                e.currentTarget.style.fontSize = '0.7em';
              }}
            >
              {adminPanelOpen ? '▼ Close Admin Panel' : '▶ Open Admin Panel'}
            </button>
          </div>

          {adminPanelOpen && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 'bold', color: '#6b7280' }}>Fee Schedule Management</h3>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#6b7280' }}>
                  Upload Fee Schedule (Excel or CSV file):
                </label>
                <div
                  style={{
                    display: 'flex',
                    position: 'relative',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#1ebaa1',
                    color: '#fff',
                    borderStyle: 'none',
                    padding: '0.7em 1.4em',
                    borderRadius: '0.5em',
                    overflow: 'hidden',
                    zIndex: 1,
                    boxShadow: '4px 8px 10px -3px rgba(0, 0, 0, 0.356)',
                    transition: 'all 250ms',
                    cursor: 'pointer',
                    width: 'fit-content',
                    fontSize: '0.9em'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#16a085';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#1ebaa1';
                  }}
                >
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                    style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: 'pointer'
                    }}
                  />
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginRight: '0.7em' }}
                  >
                    {/* Excel-like spreadsheet icon */}
                    <rect x="3" y="2" width="18" height="20" rx="2" ry="2" fill="currentColor" stroke="white" strokeWidth="1"/>
                    {/* Grid lines to represent spreadsheet cells */}
                    <line x1="3" y1="7" x2="21" y2="7" stroke="white" strokeWidth="1"/>
                    <line x1="3" y1="12" x2="21" y2="12" stroke="white" strokeWidth="1"/>
                    <line x1="3" y1="17" x2="21" y2="17" stroke="white" strokeWidth="1"/>
                    <line x1="8" y1="2" x2="8" y2="22" stroke="white" strokeWidth="1"/>
                    <line x1="13" y1="2" x2="13" y2="22" stroke="white" strokeWidth="1"/>
                    <line x1="18" y1="2" x2="18" y2="22" stroke="white" strokeWidth="1"/>
                    {/* "X" in corner to represent Excel */}
                    <text x="5" y="6" fill="white" fontSize="3" fontWeight="bold">X</text>
                  </svg>
                  Upload Fee Schedule
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                  Supported formats: Excel (.xlsx, .xls), CSV (.csv)<br/>
                  <strong>Required columns:</strong> CPT Code, Charge, Multiple Procedure Indicator, Endo Base (for indicator 3), Bilateral Procedure Indicator, Insurance columns
                </div>
              </div>

              {/* Insurance Endoscopy Settings */}
              <div style={{ marginBottom: '20px', borderTop: '1px solid #fecaca', paddingTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: endoscopySettingsOpen ? '16px' : '0' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#6b7280', margin: 0 }}>Insurance Endoscopy Settings</h3>
                  <button
                    onClick={(e) => {
                      const button = e.currentTarget;
                      const newState = !endoscopySettingsOpen;
                      setEndoscopySettingsOpen(newState);
                      if (newState) {
                        loadInsuranceEndoscopySettings();
                      }
                      // Force immediate style update after click
                      const color = newState ? 'lightcoral' : '#1d87b9';
                      button.style.background = color;
                      button.style.borderColor = color;
                      button.style.color = 'white';
                    }}
                    style={{
                      width: '166px',
                      height: '32px',
                      border: endoscopySettingsOpen ? '3px solid lightcoral' : '3px solid #1d87b9',
                      borderRadius: '18px',
                      transition: 'all 0.3s',
                      cursor: 'pointer',
                      background: 'white',
                      fontSize: '0.7em',
                      fontWeight: '550',
                      color: endoscopySettingsOpen ? 'lightcoral' : '#1d87b9',
                      fontFamily: modernFontFamily
                    }}
                    onMouseEnter={(e) => {
                      const color = endoscopySettingsOpen ? 'lightcoral' : '#1d87b9';
                      e.currentTarget.style.background = color;
                      e.currentTarget.style.borderColor = color;
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.fontSize = '0.8em';
                    }}
                    onMouseLeave={(e) => {
                      const color = endoscopySettingsOpen ? 'lightcoral' : '#1d87b9';
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.borderColor = color;
                      e.currentTarget.style.color = color;
                      e.currentTarget.style.fontSize = '0.7em';
                    }}
                  >
                    {endoscopySettingsOpen ? '▼ Hide Settings' : '▶ Configure Endoscopy'}
                  </button>
                </div>

                {endoscopySettingsOpen && (
                  <>
                    <div style={{ 
                      marginBottom: '16px', 
                      padding: '12px', 
                      backgroundColor: '#f0f9ff', 
                      border: '1px solid #0ea5e9', 
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#0369a1'
                    }}>
                      <strong>Configure endoscopy MPPR logic per insurance carrier:</strong>
                      <ul style={{ margin: '8px 0 0 20px', lineHeight: '1.4' }}>
                        <li><strong>Enhanced (default):</strong> Advanced family-based endoscopy MPPR with base code logic</li>
                        <li><strong>Standard:</strong> Traditional endoscopy MPPR (treat indicator 3 like indicator 2)</li>
                      </ul>
                      Only configure standard MPPR for carriers that specifically don't support endoscopy family logic.
                    </div>

                    <div style={{ 
                      marginBottom: '16px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      backgroundColor: '#white'
                    }}>
                      {insuranceCarriers.filter(carrier => carrier !== 'None').length === 0 ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                          No insurance carriers found. Upload a fee schedule first.
                        </div>
                      ) : (
                        insuranceCarriers.filter(carrier => carrier !== 'None').map(carrier => (
                          <div key={carrier} style={{ 
                            padding: '12px', 
                            borderBottom: '1px solid #f3f4f6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <span style={{ fontWeight: '500', color: '#374151' }}>{carrier}</span>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                                <input
                                  type="radio"
                                  name={`endoscopy-${carrier}`}
                                  checked={insuranceEndoscopySettings[carrier] !== false}
                                  onChange={() => setInsuranceEndoscopySettings({
                                    ...insuranceEndoscopySettings,
                                    [carrier]: true
                                  })}
                                />
                                Enhanced
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                                <input
                                  type="radio"
                                  name={`endoscopy-${carrier}`}
                                  checked={insuranceEndoscopySettings[carrier] === false}
                                  onChange={() => setInsuranceEndoscopySettings({
                                    ...insuranceEndoscopySettings,
                                    [carrier]: false
                                  })}
                                />
                                Standard
                              </label>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <button
                      onClick={handleSaveEndoscopySettings}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'lightcoral',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Save Endoscopy Settings
                    </button>
                  </>
                )}
              </div>

              {/* Insurance Bilateral Settings */}
              <div style={{ marginBottom: '20px', borderTop: '1px solid #fecaca', paddingTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: bilateralSettingsOpen ? '16px' : '0' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#6b7280', margin: 0 }}>Insurance Bilateral Settings</h3>
                  <button
                    onClick={(e) => {
                      const button = e.currentTarget;
                      const newState = !bilateralSettingsOpen;
                      setBilateralSettingsOpen(newState);
                      if (newState) {
                        loadInsuranceBilateralSettings();
                      }
                      // Force immediate style update after click
                      const color = newState ? 'lightcoral' : '#1d87b9';
                      button.style.background = color;
                      button.style.borderColor = color;
                      button.style.color = 'white';
                    }}
                    style={{
                      width: '166px',
                      height: '32px',
                      border: bilateralSettingsOpen ? '3px solid lightcoral' : '3px solid #1d87b9',
                      borderRadius: '18px',
                      transition: 'all 0.3s',
                      cursor: 'pointer',
                      background: 'white',
                      fontSize: '0.7em',
                      fontWeight: '550',
                      color: bilateralSettingsOpen ? 'lightcoral' : '#1d87b9',
                      fontFamily: modernFontFamily
                    }}
                    onMouseEnter={(e) => {
                      const color = bilateralSettingsOpen ? 'lightcoral' : '#1d87b9';
                      e.currentTarget.style.background = color;
                      e.currentTarget.style.borderColor = color;
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.fontSize = '0.8em';
                    }}
                    onMouseLeave={(e) => {
                      const color = bilateralSettingsOpen ? 'lightcoral' : '#1d87b9';
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.borderColor = color;
                      e.currentTarget.style.color = color;
                      e.currentTarget.style.fontSize = '0.7em';
                    }}
                  >
                    {bilateralSettingsOpen ? '▼ Hide Settings' : '▶ Configure Bilateral'}
                  </button>
                </div>

                {bilateralSettingsOpen && (
                  <>
                    <div style={{ 
                      marginBottom: '16px', 
                      padding: '12px', 
                      backgroundColor: '#f0f9ff', 
                      border: '1px solid #0ea5e9', 
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#0369a1'
                    }}>
                      <strong>Configure how insurance carriers handle bilateral procedures:</strong>
                      <ul style={{ margin: '8px 0 0 20px', lineHeight: '1.4' }}>
                        <li><strong>Option 1 (150% Bilateral):</strong> Single line item with 2x charge, 1.5x allowable, then MPPR applied</li>
                        <li><strong>Option 2 (100%/50% Bilateral):</strong> Treat as 2 separate units with standard MPPR (default)</li>
                      </ul>
                    </div>

                    <div style={{ 
                      marginBottom: '16px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px',
                      backgroundColor: '#white'
                    }}>
                      {insuranceCarriers.filter(carrier => carrier !== 'None').length === 0 ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                          No insurance carriers found. Upload a fee schedule first.
                        </div>
                      ) : (
                        insuranceCarriers.filter(carrier => carrier !== 'None').map(carrier => (
                          <div key={carrier} style={{ 
                            padding: '12px', 
                            borderBottom: '1px solid #f3f4f6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <span style={{ fontWeight: '500', color: '#374151' }}>{carrier}</span>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                                <input
                                  type="radio"
                                  name={`bilateral-${carrier}`}
                                  checked={insuranceBilateralSettings[carrier] === 'option1'}
                                  onChange={() => setInsuranceBilateralSettings({
                                    ...insuranceBilateralSettings,
                                    [carrier]: 'option1'
                                  })}
                                />
                                150% Option
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                                <input
                                  type="radio"
                                  name={`bilateral-${carrier}`}
                                  checked={insuranceBilateralSettings[carrier] !== 'option1'}
                                  onChange={() => setInsuranceBilateralSettings({
                                    ...insuranceBilateralSettings,
                                    [carrier]: 'option2'
                                  })}
                                />
                                100%/50% Option
                              </label>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <button
                      onClick={handleSaveBilateralSettings}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'lightcoral',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Save Bilateral Settings
                    </button>
                  </>
                )}
              </div>

              {/* User Management Section */}
              <div style={{ marginBottom: '20px', borderTop: '1px solid #fecaca', paddingTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: userManagementOpen ? '16px' : '0' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#6b7280', margin: 0 }}>User Management</h3>
                  <button
                    onClick={(e) => {
                      const button = e.currentTarget;
                      const newState = !userManagementOpen;
                      setUserManagementOpen(newState);
                      // Clear messages and reset form when toggling
                      setAddUserMessage('');
                      setIsAddingUser(false);
                      setPendingUserData(null);
                      // Force immediate style update after click
                      const color = newState ? 'lightcoral' : '#1d87b9';
                      button.style.background = color;
                      button.style.borderColor = color;
                      button.style.color = 'white';
                    }}
                    style={{
                      width: '166px',
                      height: '32px',
                      border: userManagementOpen ? '3px solid lightcoral' : '3px solid #1d87b9',
                      borderRadius: '18px',
                      transition: 'all 0.3s',
                      cursor: 'pointer',
                      background: 'white',
                      fontSize: '0.7em',
                      fontWeight: '550',
                      color: userManagementOpen ? 'lightcoral' : '#1d87b9',
                      fontFamily: modernFontFamily
                    }}
                    onMouseEnter={(e) => {
                      const color = userManagementOpen ? 'lightcoral' : '#1d87b9';
                      e.currentTarget.style.background = color;
                      e.currentTarget.style.borderColor = color;
                      e.currentTarget.style.color = 'white';
                      // Don't increase font size for "Hide User Management" to keep it on one line
                      if (!userManagementOpen) {
                        e.currentTarget.style.fontSize = '0.8em';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const color = userManagementOpen ? 'lightcoral' : '#1d87b9';
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.borderColor = color;
                      e.currentTarget.style.color = color;
                      e.currentTarget.style.fontSize = '0.7em';
                    }}
                  >
                    {userManagementOpen ? '▼ Hide User Management' : '▶ Manage Users'}
                  </button>
                </div>

                {userManagementOpen && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={async () => {
                            setIsLoadingUsers(true);
                            setAddUserMessage(''); // Clear any existing messages
                            try {
                              const allUsers = await getAllUsersActive();
                              setUsers(allUsers);
                              const pending = await getPendingUsers();
                              setPendingUsers(pending);
                            } catch (error) {
                              console.error('Error loading users:', error);
                              setAddUserMessage('Error loading users');
                            } finally {
                              setIsLoadingUsers(false);
                            }
                          }}
                          disabled={isLoadingUsers}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: isLoadingUsers ? '#9ca3af' : '#1ebaa1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isLoadingUsers ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          {isLoadingUsers ? 'Loading...' : 'Load Users'}
                        </button>
                        <button
                          onClick={() => {
                            setIsAddingUser(!isAddingUser);
                            // Clear messages when toggling add user form
                            setAddUserMessage('');
                            setPendingUserData(null);
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: isAddingUser ? 'lightcoral' : '#1d87b9',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          {isAddingUser ? 'Cancel' : 'Add User'}
                        </button>
                        {/* Debug button removed for security - do not log passwords */}
                      </div>
                    </div>

                {/* Add User Form - STEP 1: Create Pending User */}
                {isAddingUser && (
                  <div style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #0ea5e9',
                    borderRadius: '6px'
                  }}>
                    <div style={{ marginBottom: '8px', fontSize: '11px', color: '#0369a1', fontWeight: '500' }}>
                      ℹ️ Step 1: Create pending user. Then activate below to send password email.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Email:</label>
                        <input
                          type="email"
                          placeholder="user@example.com"
                          value={pendingUserData?.email || ''}
                          onChange={(e) => setPendingUserData(prev => ({
                            ...prev,
                            email: e.target.value,
                            role: prev?.role || 'user'
                          }))}
                          style={{
                            width: '100%',
                            padding: '4px 8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Role:</label>
                        <select
                          value={pendingUserData?.role || 'user'}
                          onChange={(e) => setPendingUserData(prev => ({
                            ...prev,
                            email: prev?.email || '',
                            role: e.target.value as UserRole
                          }))}
                          style={{
                            width: '100%',
                            padding: '4px 8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      <button
                        onClick={async () => {
                          if (!pendingUserData?.email) {
                            setAddUserMessage('Email is required');
                            return;
                          }

                          // Trim whitespace from email
                          const trimmedEmail = pendingUserData.email.trim();

                          // Validate email format
                          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          if (!emailRegex.test(trimmedEmail)) {
                            setAddUserMessage('Please enter a valid email address');
                            return;
                          }

                          // Clear any existing messages before attempting creation
                          setAddUserMessage('');

                          try {
                            // STEP 1: Create pending user entry only
                            await createPendingUser(trimmedEmail, pendingUserData.role);
                            setAddUserMessage(`Pending user ${trimmedEmail} created! Click Activate below to send password email.`);
                            setPendingUserData(null);
                            setIsAddingUser(false);
                            // Reload pending users list
                            const pending = await getPendingUsers();
                            setPendingUsers(pending);
                            // Clear success message after 5 seconds
                            setTimeout(() => setAddUserMessage(''), 5000);
                          } catch (error: any) {
                            console.error('Error creating pending user:', error);
                            setAddUserMessage('Error creating pending user: ' + error.message);
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Create Pending User
                      </button>
                    </div>
                  </div>
                )}

                {/* Pending Users List */}
                {pendingUsers.length > 0 && (
                  <div style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#fef3c7',
                    border: '1px solid #f59e0b',
                    borderRadius: '6px'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#92400e' }}>
                      Pending Users (Not Activated)
                    </div>
                    <div style={{ fontSize: '11px', color: '#92400e', marginBottom: '8px' }}>
                      ⚠️ Click Activate to create auth account and send password email. You will be logged out.
                    </div>
                    {pendingUsers.map((pu) => (
                      <div key={pu.email} style={{
                        padding: '8px',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        marginBottom: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <span style={{ fontWeight: '600' }}>{pu.email}</span>
                          {' '}
                          <span style={{
                            padding: '2px 6px',
                            backgroundColor: pu.role === 'admin' ? '#dc2626' : '#3b82f6',
                            color: 'white',
                            borderRadius: '3px',
                            fontSize: '10px'
                          }}>
                            {pu.role}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => {
                              setActivateEmail(pu.email);
                              setShowActivateDialog(true);
                            }}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#059669',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}
                          >
                            Activate
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Delete pending user ${pu.email}?`)) {
                                try {
                                  await deletePendingUser(pu.email);
                                  setAddUserMessage(`Pending user ${pu.email} deleted`);
                                  const pending = await getPendingUsers();
                                  setPendingUsers(pending);
                                  setTimeout(() => setAddUserMessage(''), 3000);
                                } catch (error: any) {
                                  setAddUserMessage('Error deleting pending user: ' + error.message);
                                }
                              }
                            }}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Activate Dialog */}
                {showActivateDialog && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10000
                  }}>
                    <div style={{
                      backgroundColor: 'white',
                      padding: '24px',
                      borderRadius: '8px',
                      maxWidth: '400px',
                      width: '90%'
                    }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                        Confirm User Activation
                      </div>
                      <div style={{ fontSize: '14px', marginBottom: '16px' }}>
                        Re-enter the email address to confirm activation:
                        <div style={{ fontWeight: '600', marginTop: '8px', color: '#0369a1' }}>
                          {activateEmail}
                        </div>
                      </div>
                      <input
                        type="email"
                        placeholder="Confirm email address"
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          marginBottom: '16px',
                          fontSize: '14px'
                        }}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && e.currentTarget.value.toLowerCase() === activateEmail.toLowerCase()) {
                            try {
                              await activatePendingUser(activateEmail);
                              // User will be logged out, so no need to update UI
                            } catch (error: any) {
                              alert('Error activating user: ' + error.message);
                              setShowActivateDialog(false);
                            }
                          }
                        }}
                      />
                      <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '16px' }}>
                        ⚠️ This will create the auth account, send password reset email, and log you out.
                      </div>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => {
                            setShowActivateDialog(false);
                            setActivateEmail('');
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            const confirmInput = document.querySelector('input[type="email"]') as HTMLInputElement;
                            if (confirmInput.value.toLowerCase() !== activateEmail.toLowerCase()) {
                              alert('Email addresses do not match!');
                              return;
                            }
                            try {
                              // Remove from pending users list immediately
                              setPendingUsers(prev => prev.filter(pu => pu.email !== activateEmail));
                              setShowActivateDialog(false);

                              await activatePendingUser(activateEmail);
                              // User will be logged out automatically
                            } catch (error: any) {
                              alert('Error activating user: ' + error.message);
                              // Reload pending users if activation failed
                              const pending = await getPendingUsers();
                              setPendingUsers(pending);
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#059669',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          Activate & Send Email
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* User List */}
                <div style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: '#fff'
                }}>
                  {users.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                      No users loaded. Click "Load Users" to see all users.
                    </div>
                  ) : (
                    users.map((user, index) => (
                      <div key={user.uid} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        borderBottom: index < users.length - 1 ? '1px solid #e5e7eb' : 'none'
                      }}>
                        <div>
                          <div style={{ fontWeight: '500', fontSize: '14px' }}>{user.email}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            Role: {user.role}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <select
                            value={user.role}
                            onChange={async (e) => {
                              setAddUserMessage(''); // Clear existing messages
                              try {
                                await updateUserRole(user.uid, e.target.value as UserRole);
                                setAddUserMessage(`User ${user.email} role updated to ${e.target.value}`);
                                // Reload users list
                                const allUsers = await getAllUsersActive();
                                setUsers(allUsers);
                                // Clear success message after 3 seconds
                                setTimeout(() => setAddUserMessage(''), 3000);
                              } catch (error: any) {
                                setAddUserMessage('Error updating role: ' + error.message);
                              }
                            }}
                            style={{
                              padding: '4px 6px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '10px'
                            }}
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete user ${user.email}?`)) {
                                setAddUserMessage(''); // Clear existing messages
                                try {
                                  await deleteUserClientSide(user.uid);
                                  setAddUserMessage(`User ${user.email} deleted successfully`);
                                  // Reload users list
                                  const allUsers = await getAllUsersActive();
                                  setUsers(allUsers);
                                  // Clear success message after 3 seconds
                                  setTimeout(() => setAddUserMessage(''), 3000);
                                } catch (error: any) {
                                  setAddUserMessage('Error deleting user: ' + error.message);
                                }
                              }
                            }}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '10px',
                              fontWeight: '500'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Status Message */}
                {addUserMessage && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: addUserMessage.includes('Error') ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${addUserMessage.includes('Error') ? '#fecaca' : '#bbf7d0'}`,
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: addUserMessage.includes('Error') ? '#dc2626' : '#059669'
                  }}>
                    {addUserMessage}
                  </div>
                )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Main Calculator Form */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Left Column - Input Form */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '24px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          height: 'fit-content'
        }}>
          <h2 style={{ marginBottom: '20px', color: '#1d87b9' }}>Patient Information</h2>
          
          {/* Patient ID - Disabled to avoid storing PHI */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              Patient ID:
            </label>
            <input
              type="text"
              value={calculatorData.patientId}
              disabled
              style={{
                ...modernInputStyle,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#f3f4f6',
                cursor: 'not-allowed',
                color: '#9ca3af'
              }}
              placeholder="(Can be written on printed version)"
            />
          </div>

          {/* CPT Codes with Bilateral Support */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: '500',
              color: fieldErrors.cptCodes ? '#dc2626' : '#374151'
            }}>
              CPT Codes:
            </label>

            {calculatorData.cptCodes.map((entry, index) => (
              <div key={index} style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '8px',
                alignItems: 'center',
                padding: '8px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                backgroundColor: '#f9fafb'
              }}>
                {/* CPT Code input with internal label */}
                <div style={{ flex: 2, position: 'relative', minWidth: 0 }}>
                  <label style={{
                    position: 'absolute',
                    top: '6px',
                    left: '12px',
                    fontSize: '9px',
                    color: '#6b7280',
                    pointerEvents: 'none',
                    fontWeight: '500',
                    zIndex: 1
                  }}>
                    Code
                  </label>
                  <input
                    type="text"
                    value={entry.code}
                    onChange={(e) => handleCPTCodesChange(index, 'code', e.target.value)}
                    style={{
                      ...modernInputStyle,
                      width: '100%',
                      boxSizing: 'border-box',
                      paddingTop: '20px',
                      paddingBottom: '6px'
                    }}
                  />
                </div>

                {/* Units input with internal label */}
                <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                  <label style={{
                    position: 'absolute',
                    top: '6px',
                    left: '12px',
                    fontSize: '9px',
                    color: '#6b7280',
                    pointerEvents: 'none',
                    fontWeight: '500',
                    zIndex: 1
                  }}>
                    Units
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={entry.units}
                    onChange={(e) => handleCPTCodesChange(index, 'units', e.target.value)}
                    style={{
                      ...modernInputStyle,
                      width: '100%',
                      boxSizing: 'border-box',
                      paddingTop: '20px',
                      paddingBottom: '6px'
                    }}
                  />
                </div>

                {/* Modifier dropdown with internal label - only show if modifiers available */}
                {cptModifiers[index] && cptModifiers[index].length > 0 && (
                  <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                    <label style={{
                      position: 'absolute',
                      top: '6px',
                      left: '12px',
                      fontSize: '9px',
                      color: '#6b7280',
                      pointerEvents: 'none',
                      fontWeight: '500',
                      zIndex: 1
                    }}>
                      Modifier
                    </label>
                    <select
                      value={entry.modifier || ''}
                      onChange={(e) => handleCPTCodesChange(index, 'modifier', e.target.value)}
                      style={{
                        ...modernInputStyle,
                        width: '100%',
                        boxSizing: 'border-box',
                        paddingTop: '20px',
                        paddingBottom: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">None</option>
                      {cptModifiers[index]
                        .filter(mod => mod !== '')
                        .map(modifier => (
                          <option key={modifier} value={modifier}>
                            {modifier}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer'
                }}>
                  <div style={iosCheckboxStyle}>
                    <input
                      type="checkbox"
                      checked={entry.bilateral || false}
                      onChange={(e) => handleCPTCodesChange(index, 'bilateral', e.target.checked)}
                      style={{ display: 'none' }}
                    />
                    <div style={checkboxWrapperStyle}>
                      <div style={entry.bilateral ? checkboxBgCheckedStyle : checkboxBgStyle}></div>
                      <div style={entry.bilateral ? checkboxIconCheckedStyle : checkboxIconStyle}>
                        ✓
                      </div>
                    </div>
                  </div>
                  <span style={bilateralLabelStyle}>Bilateral</span>
                </label>
                {calculatorData.cptCodes.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeCPTCodeField(index)}
                    style={{
                      width: '33px',
                      height: '33px',
                      borderRadius: '50%',
                      backgroundColor: '#6b7280',
                      border: 'none',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.164)',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      const button = e.target as HTMLButtonElement;
                      const svg = button.querySelector('svg') as SVGElement;
                      const deleteText = button.querySelector('[data-delete-text]') as HTMLSpanElement;

                      button.style.width = '93px';
                      button.style.borderRadius = '33px';
                      button.style.backgroundColor = 'rgb(255, 69, 69)';

                      if (svg) {
                        svg.style.opacity = '0';
                        svg.style.transform = 'scale(0)';
                      }

                      if (deleteText) {
                        deleteText.style.fontSize = '13px';
                        deleteText.style.opacity = '1';
                        deleteText.style.transform = 'translateY(30px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const button = e.target as HTMLButtonElement;
                      const svg = button.querySelector('svg') as SVGElement;
                      const deleteText = button.querySelector('[data-delete-text]') as HTMLSpanElement;

                      button.style.width = '33px';
                      button.style.borderRadius = '50%';
                      button.style.backgroundColor = '#6b7280';

                      if (svg) {
                        svg.style.opacity = '1';
                        svg.style.transform = 'scale(1)';
                      }

                      if (deleteText) {
                        deleteText.style.fontSize = '2px';
                        deleteText.style.opacity = '0';
                        deleteText.style.transform = 'translateY(-20px)';
                      }
                    }}
                  >
                    <svg
                      style={{
                        width: '12px',
                        transition: 'all 0.3s'
                      }}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 6h18l-2 13H5L3 6zM8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2M10 11v6M14 11v6"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="white"
                      />
                      <line
                        x1="3"
                        y1="8"
                        x2="21"
                        y2="8"
                        stroke="black"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    <span
                      data-delete-text="true"
                      style={{
                        position: 'absolute',
                        top: '-20px',
                        color: 'white',
                        transition: 'all 0.3s',
                        fontSize: '2px',
                        opacity: 0,
                        transform: 'translateY(-20px)',
                        pointerEvents: 'none'
                      }}
                    >
                      Delete
                    </span>
                  </button>
                )}
              </div>
            ))}
            
            <button
              type="button"
              onClick={addCPTCodeField}
              style={{
                width: '120px',
                height: '30px',
                border: '3px solid lightcoral',
                borderRadius: '22px',
                transition: 'all 0.3s',
                cursor: 'pointer',
                background: 'white',
                fontSize: '0.8em',
                fontWeight: '550',
                color: 'lightcoral',
                fontFamily: modernFontFamily
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'lightcoral';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.fontSize = '0.9em';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = 'lightcoral';
                e.currentTarget.style.fontSize = '0.8em';
              }}
            >
              Add CPT Code
            </button>

            {fieldErrors.cptCodes && (
              <div style={{ marginTop: '4px', color: '#dc2626', fontSize: '12px' }}>
                Please enter at least one CPT code
              </div>
            )}
          </div>

          {/* Insurance Carrier */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
              Insurance Carrier:
            </label>
            <select
              value={calculatorData.insuranceCarrier}
              onChange={(e) => handleInsuranceChange(e.target.value)}
              style={modernDropdownStyle}
            >
              <option value="None">Self-Pay / No Insurance</option>
              {insuranceCarriers.map(carrier => (
                <option key={carrier} value={carrier}>{carrier}</option>
              ))}
            </select>
          </div>

          {/* Insurance-specific fields */}
          {calculatorData.insuranceCarrier !== 'None' && (
            <>
              {/* Medigap Plan - only show for Medicare carriers */}
              {calculatorData.insuranceCarrier.toLowerCase().includes('medicare') && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                    Medigap Plan:
                  </label>
                  <select
                    value={calculatorData.medigapPlan}
                    onChange={(e) => {
                      const plan = e.target.value;
                      let defaults: any = { medigapPlan: plan };

                      // Set defaults based on Medigap plan
                      switch (plan) {
                        case 'None':
                          defaults.deductibleRemaining = '';
                          defaults.outOfPocketRemaining = '';
                          defaults.coinsurance = '20';
                          defaults.hasCopay = 'No';
                          defaults.copayAmount = '';
                          break;
                        case 'Plan A':
                        case 'Plan B':
                        case 'Plan D':
                        case 'Plan M':
                          defaults.deductibleRemaining = '';
                          defaults.outOfPocketRemaining = '';
                          defaults.coinsurance = '0';
                          defaults.hasCopay = 'No';
                          defaults.copayAmount = '';
                          break;
                        case 'Plan C':
                        case 'Plan F':
                          defaults.deductibleRemaining = '0';
                          defaults.outOfPocketRemaining = '';
                          defaults.coinsurance = '0';
                          defaults.hasCopay = 'No';
                          defaults.copayAmount = '';
                          break;
                        case 'Plan G':
                          defaults.deductibleRemaining = '';
                          defaults.outOfPocketRemaining = '';
                          defaults.coinsurance = '0';
                          defaults.hasCopay = 'No';
                          defaults.copayAmount = '';
                          break;
                        case 'Plan K':
                          defaults.deductibleRemaining = '';
                          defaults.outOfPocketRemaining = '';
                          defaults.coinsurance = '10';
                          defaults.hasCopay = 'No';
                          defaults.copayAmount = '';
                          break;
                        case 'Plan L':
                          defaults.deductibleRemaining = '';
                          defaults.outOfPocketRemaining = '';
                          defaults.coinsurance = '5';
                          defaults.hasCopay = 'No';
                          defaults.copayAmount = '';
                          break;
                        case 'Plan N':
                          defaults.deductibleRemaining = '';
                          defaults.outOfPocketRemaining = '';
                          defaults.coinsurance = '0';
                          defaults.hasCopay = 'Yes';
                          defaults.copayAmount = '20';
                          break;
                      }

                      setCalculatorData({ ...calculatorData, ...defaults });
                      setResults(null); // Clear results when medigap plan changes
                      setFieldErrors({}); // Clear all field errors when changing medigap plan
                    }}
                    style={modernDropdownStyle}
                  >
                    <option value="None">None</option>
                    <option value="Plan A">Plan A</option>
                    <option value="Plan B">Plan B</option>
                    <option value="Plan C">Plan C</option>
                    <option value="Plan D">Plan D</option>
                    <option value="Plan F">Plan F</option>
                    <option value="Plan G">Plan G</option>
                    <option value="Plan K">Plan K</option>
                    <option value="Plan L">Plan L</option>
                    <option value="Plan M">Plan M</option>
                    <option value="Plan N">Plan N</option>
                  </select>
                </div>
              )}

              {/* Three insurance fields on one line with proper spacing */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.6fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: fieldErrors.deductibleRemaining ? '#dc2626' : '#374151'
                  }}>
                    Deductible Remaining:
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={calculatorData.deductibleRemaining}
                    disabled={calculatorData.insuranceCarrier.toLowerCase().includes('medicare') &&
                             (calculatorData.medigapPlan === 'Plan C' || calculatorData.medigapPlan === 'Plan F')}
                    onChange={(e) => {
                      setCalculatorData({ ...calculatorData, deductibleRemaining: e.target.value });
                      if (fieldErrors.deductibleRemaining) {
                        setFieldErrors({ ...fieldErrors, deductibleRemaining: false });
                      }
                    }}
                    style={{
                      ...modernInputStyle,
                      border: fieldErrors.deductibleRemaining ? '2px solid #fca5a5' : 'none',
                      width: '85%',
                      cursor: (calculatorData.insuranceCarrier.toLowerCase().includes('medicare') &&
                              (calculatorData.medigapPlan === 'Plan C' || calculatorData.medigapPlan === 'Plan F')) ? 'not-allowed' : 'text'
                    }}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: fieldErrors.outOfPocketRemaining ? '#dc2626' : '#374151'
                  }}>
                    Out of Pocket Remaining:
                  </label>
                  <input
                    type="text"
                    value={(calculatorData.insuranceCarrier.toLowerCase().includes('medicare') &&
                           calculatorData.medigapPlan !== 'Plan K' &&
                           calculatorData.medigapPlan !== 'Plan L') ? 'Not Applicable' : calculatorData.outOfPocketRemaining}
                    disabled={calculatorData.insuranceCarrier.toLowerCase().includes('medicare') &&
                             calculatorData.medigapPlan !== 'Plan K' &&
                             calculatorData.medigapPlan !== 'Plan L'}
                    onChange={(e) => {
                      setCalculatorData({ ...calculatorData, outOfPocketRemaining: e.target.value });
                      if (fieldErrors.outOfPocketRemaining) {
                        setFieldErrors({ ...fieldErrors, outOfPocketRemaining: false });
                      }
                    }}
                    style={{
                      ...modernInputStyle,
                      border: fieldErrors.outOfPocketRemaining ? '2px solid #fca5a5' : 'none',
                      width: '85%',
                      cursor: (calculatorData.insuranceCarrier.toLowerCase().includes('medicare') &&
                              calculatorData.medigapPlan !== 'Plan K' &&
                              calculatorData.medigapPlan !== 'Plan L') ? 'not-allowed' : 'text'
                    }}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: fieldErrors.coinsurance ? '#dc2626' : '#374151'
                  }}>
                    Coinsurance %:
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={calculatorData.coinsurance}
                    disabled={calculatorData.insuranceCarrier.toLowerCase().includes('medicare')}
                    onChange={(e) => {
                      setCalculatorData({ ...calculatorData, coinsurance: e.target.value });
                      if (fieldErrors.coinsurance) {
                        setFieldErrors({ ...fieldErrors, coinsurance: false });
                      }
                    }}
                    style={{
                      ...modernInputStyle,
                      border: fieldErrors.coinsurance ? '2px solid #fca5a5' : 'none',
                      width: '70%',
                      cursor: calculatorData.insuranceCarrier.toLowerCase().includes('medicare') ? 'not-allowed' : 'text'
                    }}
                    placeholder="20"
                  />
                </div>
              </div>

              {/* Copay Section - Combined on single line */}
              <div style={{ display: 'grid', gridTemplateColumns: '2.6fr 1.4fr', gap: '40px', marginBottom: '20px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Does patient have E&M copay?
                  </label>
                  <select
                    value={calculatorData.hasCopay}
                    onChange={(e) => setCalculatorData({
                      ...calculatorData,
                      hasCopay: e.target.value,
                      copayAmount: e.target.value === 'No' ? '' : calculatorData.copayAmount
                    })}
                    style={{
                      ...modernDropdownStyle,
                      width: '85%'
                    }}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: fieldErrors.copayAmount ? '#dc2626' : '#374151'
                  }}>
                    Office Visit Copay:
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={calculatorData.copayAmount}
                    onChange={(e) => {
                      setCalculatorData({ ...calculatorData, copayAmount: e.target.value });
                      if (fieldErrors.copayAmount) {
                        setFieldErrors({ ...fieldErrors, copayAmount: false });
                      }
                    }}
                    style={{
                      ...modernInputStyle,
                      border: fieldErrors.copayAmount ? '2px solid #fca5a5' : 'none',
                      width: '82%'
                    }}
                    placeholder="25.00"
                    disabled={calculatorData.hasCopay === 'No'}
                  />
                </div>
              </div>
            </>
          )}

          {/* Calculate Button */}
          <button
            onClick={handleCalculate}
            disabled={!feeSchedule}
            style={{
              width: '288px',
              height: '64px',
              border: !feeSchedule ? '3px solid #9ca3af' : '3px solid #1d87b9',
              borderRadius: '35px',
              transition: 'all 0.3s',
              cursor: !feeSchedule ? 'not-allowed' : 'pointer',
              background: 'white',
              fontSize: '1.1em',
              fontWeight: '550',
              color: !feeSchedule ? '#9ca3af' : '#1d87b9',
              fontFamily: modernFontFamily,
              margin: '0 auto',
              display: 'block'
            }}
            onMouseEnter={(e) => {
              if (feeSchedule) {
                e.currentTarget.style.background = '#1d87b9';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.fontSize = '1.3em';
              }
            }}
            onMouseLeave={(e) => {
              if (feeSchedule) {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#1d87b9';
                e.currentTarget.style.fontSize = '1.1em';
              }
            }}
          >
            {!feeSchedule ? 'No Fee Schedule Loaded' : 'Calculate Payment'}
          </button>
        </div>

        {/* Right Column - Results */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '24px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          height: 'fit-content'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0, color: '#1d87b9' }}>Calculation Results</h2>
            {results && (
              <button
                onClick={handlePrint}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#1ebaa1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                🖨️ Print
              </button>
            )}
          </div>

          {!results ? (
            <div style={{ 
              textAlign: 'center', 
              color: '#6b7280', 
              padding: '40px',
              border: '2px dashed #e5e7eb',
              borderRadius: '8px'
            }}>
              <p style={{ fontSize: '16px', marginBottom: '8px' }}>No calculations yet</p>
              <p style={{ fontSize: '14px' }}>Enter patient information and click "Calculate Payment" to see results.</p>
            </div>
          ) : (
            <div>
              {/* Procedure Details */}
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '6px'
              }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#475569', fontSize: '16px' }}>
                  Procedure Details
                </h3>
                <div style={{
                  fontSize: '14px',
                  lineHeight: '1.8',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  color: '#334155',
                  fontFamily: 'inherit'
                }}
                dangerouslySetInnerHTML={{ __html: generateProcedureDetails(results) }}
                />
              </div>

              {/* Summary */}
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#ecfdf5',
                border: '1px solid #10b981',
                borderRadius: '6px'
              }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#059669', fontSize: '16px' }}>
                  Summary
                </h3>
                
                <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                  <div style={{ marginBottom: '6px' }}>
                    <span>Total Charge:</span>
                    <span style={{ float: 'right', fontWeight: 'bold' }}>${results.totalCharge.toFixed(2)}</span>
                  </div>
                  
                  {calculatorData.insuranceCarrier !== 'None' && (
                    <>
                      <div style={{ marginBottom: '6px' }}>
                        <span>Insurance Approved Amount:</span>
                        <span style={{ float: 'right', fontWeight: 'bold' }}>${results.mpprAdjustedTotal.toFixed(2)}</span>
                      </div>
                      
                      <div style={{ marginBottom: '6px' }}>
                        <span>Deductible Due:</span>
                        <span style={{ float: 'right' }}>${results.deductibleDue.toFixed(2)}</span>
                      </div>

                      {results.copayDue > 0 && (
                        <div style={{ marginBottom: '6px' }}>
                          <span>Copay Due:</span>
                          <span style={{ float: 'right' }}>${results.copayDue.toFixed(2)}</span>
                        </div>
                      )}

                      <div style={{ marginBottom: '6px' }}>
                        <span>Your Remaining Share ({calculatorData.coinsurance}%):</span>
                        <span style={{ float: 'right' }}>${results.coinsuranceDue.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Final Payment Amount */}
                {calculatorData.insuranceCarrier !== 'None' ? (
                  <div style={{
                    borderTop: '1px solid #10b981',
                    paddingTop: '12px',
                    marginTop: '12px',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    <div style={{ marginBottom: '4px' }}>
                      <span>Total Patient Payment:</span>
                      <span style={{ float: 'right', color: '#2563eb' }}>${results.patientPayment.toFixed(2)}</span>
                    </div>
                    {results.isCappedByOOP && (
                      <div style={{
                        marginTop: '8px',
                        padding: '6px',
                        backgroundColor: '#fef3cd',
                        border: '1px solid #f59e0b',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#92400e'
                      }}>
                        ⚠️ Payment capped by out-of-pocket maximum.
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{
                    borderTop: '1px solid #10b981',
                    paddingTop: '12px',
                    marginTop: '12px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#059669'
                  }}>
                    <div style={{ marginBottom: '4px' }}>
                      <span>Self-Pay Amount:</span>
                      <span style={{ float: 'right', color: '#059669' }}>${results.slidingFeePayment.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* MPPR and Bilateral explanation */}
              {calculatorData.insuranceCarrier !== 'None' && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f0f9ff', 
                  border: '1px solid #0ea5e9', 
                  borderRadius: '6px',
                  fontSize: '11px',
                  color: '#0369a1'
                }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#0369a1' }}>
                    Multiple Procedure Discount{hasBilateralProcedures(results) && results.usedBilateralOption ? ` + ${results.usedBilateralOption === 'option1' ? '150%' : '100%/50%'} Both-Sides Logic` : ''} Applied
                  </h4>
                  <div style={{ lineHeight: '1.4' }}>
                    {results.usedEnhancedEndoscopy ? (
                      <div>
                        <strong>Multiple Procedure Discount Applied:</strong>
                        <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
                          <li>• Your insurance allows 100% for the primary procedure</li>
                          <li>• Your insurance allows a reduced amount for additional related procedures</li>
                          <li>• This is how insurance typically handles multiple procedures done together</li>
                        </ul>
                      </div>
                    ) : (
                      <div><strong>Multiple Procedure Discount:</strong> Your insurance typically pays 100% of the first procedure and 50% of additional procedures.</div>
                    )}
                    {hasBilateralProcedures(results) && (
                      <div style={{ marginTop: '12px' }}>
                        <strong>Both-Sides Procedures:</strong> {results.usedBilateralOption === 'option1' ?
                          'Your insurance typically allows 150% of the single-side amount.' :
                          'Your insurance typically pays 100% of the first side and 50% of the second side.'
                        }
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicalBillingApp;